/**
 * Puzzle Customizer — All phases
 *
 * Architecture:
 *   StageManager  — Named stage navigation (not index-based)
 *   BulkManager   — Bulk-specific state: CSV/manual address + name entry, enhanced CSV, order types
 *   PuzzleCustomizer — Main controller: modal, image pipeline, crop, preview, cart, pricing
 *
 * Phases:
 *   1. Scaffold: stage manager, modal, piece count selector
 *   2. Image pipeline: validation, HEIC conversion, downscaling, blank detection
 *   3. Crop: Cropper.js, aspect ratio lock, zoom, rotate
 *   4. Preview: box/puzzle composite, note/image/logo options, variant resolution
 *   5. Cart: S3 upload, add-to-cart, cart count, processing/success stages
 *   6. Bulk: shipping cards, CSV upload, manual entry, personalization, enhanced CSV, multi-recipient
 */

/* ========================================================================
   Stage Manager — tracks stages by name, not index
   ======================================================================== */

const StageManager = class {
  /** @param {string[]} stages - ordered stage names */
  constructor(stages, { onTransition, log } = {}) {
    this.allStages = stages;
    this.activeStages = [...stages];
    this.currentStageName = stages[0];
    this.onTransition = onTransition || (() => {});
    this.log = log || (() => {});
  }

  current() {
    return this.currentStageName;
  }

  goTo(name) {
    if (!this.activeStages.includes(name)) return false;
    const from = this.currentStageName;
    this.currentStageName = name;
    this.onTransition(from, name);
    this.log(`Stage: ${from} → ${name}`);
    return true;
  }

  goForward() {
    const idx = this.activeStages.indexOf(this.currentStageName);
    if (idx < 0 || idx >= this.activeStages.length - 1) return false;
    return this.goTo(this.activeStages[idx + 1]);
  }

  goBack() {
    const idx = this.activeStages.indexOf(this.currentStageName);
    if (idx <= 0) return false;
    return this.goTo(this.activeStages[idx - 1]);
  }

  enableStage(name) {
    if (!this.allStages.includes(name) || this.activeStages.includes(name)) return;
    const originalIdx = this.allStages.indexOf(name);
    let insertIdx = 0;
    for (let i = 0; i < this.activeStages.length; i++) {
      if (this.allStages.indexOf(this.activeStages[i]) < originalIdx) {
        insertIdx = i + 1;
      }
    }
    this.activeStages.splice(insertIdx, 0, name);
  }

  disableStage(name) {
    if (this.currentStageName === name) return false;
    this.activeStages = this.activeStages.filter(s => s !== name);
    return true;
  }
};

/* ========================================================================
   Bulk Manager — handles all bulk-specific state and logic
   ======================================================================== */

const BulkManager = class {
  /** @param {object} config - PZY_CONFIG reference */
  constructor(config, { log } = {}) {
    this.config = config;
    this.strings = config.strings || {};
    this.log = log || (() => {});

    this.csvNames = [];
    this.manualNames = [];
    this.namesMode = 'csv';
    this.csvAddresses = [];
    this.manualAddresses = [];
    this.recipientMode = 'csv';
    this.activeTokens = { firstName: false, lastName: false };
    this.quantity = 1;
    this.previewIndex = 0;
    this.addressPreviewIndex = 0;
    this.nonPersonalizedPreviewIndex = 0;
    this.personalizationEnabled = false;
    this.editingAddressIndex = null;
    this.editingNameIndex = null;

    this.NAME_PATTERN = /^[\p{L}\s'\-]+$/u;
    this.VALID_STATES = new Set([
      'AL','AK','AR','AZ','CA','CO','CT','DC','DE','FL','GA','HI',
      'IA','ID','IL','IN','KS','KY','LA','MA','MD','ME',
      'MI','MN','MO','MS','MT','NC','ND','NE','NH','NJ',
      'NM','NV','NY','OH','OK','OR','PA','RI','SC','SD',
      'TN','TX','UT','VA','VT','WA','WI','WV','WY'
    ]);
  }

  /** Returns the active names list based on the current names input mode. */
  get personalizedNames() {
    return this.namesMode === 'csv' ? this.csvNames : this.manualNames;
  }

  /** Returns the active address list based on the current input mode. */
  get recipientAddresses() {
    return this.recipientMode === 'csv' ? this.csvAddresses : this.manualAddresses;
  }

  init() {
    this._initShippingCards();
    this._initQuantitySelector();
    this._initCsvWidgets();
    this._initManualEntry();
    this._initManualNames();
    this._initPreviewPagers();
    this.log('BulkManager initialized');
  }

  // ── Shipping card selection ──

  _initShippingCards() {
    const container = document.querySelector('.pzy-bulk-shipping-options');
    if (!container) return;
    container.addEventListener('click', (e) => {
      const card = e.target.closest('.pzy-bulk-shipping-card');
      if (!card) return;
      container.querySelectorAll('.pzy-bulk-shipping-card').forEach(c => {
        c.classList.remove('is-selected');
        c.setAttribute('aria-checked', 'false');
      });
      card.classList.add('is-selected');
      card.setAttribute('aria-checked', 'true');
      this._updateVisibility();
      this._updateAddToCartState();
    });
  }

  getSelectedShipping() {
    const selected = document.querySelector('.pzy-bulk-shipping-card.is-selected');
    return selected?.dataset.shippingOption || null;
  }

  // ── Visibility toggling ──

  _updateVisibility() {
    const singleCard = document.querySelector('[data-shipping-option="single"]');
    const multiCard = document.querySelector('[data-shipping-option="multiple"]');
    const quantityEl = singleCard?.querySelector('.pzy-bulk-quantity');
    const namesSection = singleCard?.querySelector('.pzy-names-section');
    const multiRecipients = multiCard?.querySelector('.pzy-multi-recipients');

    [quantityEl, namesSection].forEach(el => { if (el) el.style.display = 'none'; });
    if (multiRecipients) multiRecipients.style.display = 'none';

    if (singleCard?.classList.contains('is-selected')) {
      if (this.personalizationEnabled) {
        if (namesSection) {
          namesSection.style.display = '';
          this._updateNameFieldVisibility();
        }
      } else {
        if (quantityEl) quantityEl.style.display = 'flex';
      }
    } else if (multiCard?.classList.contains('is-selected')) {
      if (multiRecipients) multiRecipients.style.display = '';
    }
  }

  // ── Add-to-cart state ──

  _updateAddToCartState() {
    const btn = document.getElementById('pzy-bulk-add-to-cart');
    if (!btn) return;

    const shipping = this.getSelectedShipping();
    let canAdd = false;

    if (shipping === 'single') {
      if (this.personalizationEnabled) {
        canAdd = this.personalizedNames.length > 0;
      } else {
        canAdd = true;
      }
    } else if (shipping === 'multiple') {
      canAdd = this.recipientAddresses.length > 0;
    }

    btn.disabled = !canAdd;
  }

  // ── Quantity selector ──

  _initQuantitySelector() {
    const minus = document.querySelector('.pzy-bulk-quantity-minus');
    const plus = document.querySelector('.pzy-bulk-quantity-plus');
    const input = document.getElementById('pzy-bulk-quantity-input');
    if (!minus || !plus || !input) return;

    const update = (val) => {
      let v = parseInt(val, 10);
      if (isNaN(v) || v < 1) v = 1;
      input.value = v;
      this.quantity = v;
    };

    minus.addEventListener('click', () => update(parseInt(input.value, 10) - 1));
    plus.addEventListener('click', () => update(parseInt(input.value, 10) + 1));
    input.addEventListener('change', () => update(input.value));
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); input.blur(); } });
  }

  // ── CSV upload widgets ──

  _initCsvWidgets() {
    const nameWidget = document.querySelector('.pzy-csv-widget-names');
    const addressWidget = document.querySelector('.pzy-csv-widget-addresses');

    if (nameWidget) {
      nameWidget.addEventListener('click', (e) => {
        const actionBtn = e.target.closest('[data-action]');
        if (!actionBtn) return;
        const action = actionBtn.dataset.action;
        if (action === 'upload' || action === 're-upload') this._triggerFileUpload('names');
        else if (action === 'delete' || action === 'reset') this._resetNameUpload();
      });
    }

    if (addressWidget) {
      addressWidget.addEventListener('click', (e) => {
        const actionBtn = e.target.closest('[data-action]');
        if (!actionBtn) return;
        const action = actionBtn.dataset.action;
        if (action === 'upload' || action === 're-upload') this._triggerFileUpload('addresses');
        else if (action === 'delete' || action === 'reset') this._resetAddressUpload();
      });
    }
  }

  _triggerFileUpload(type) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv, text/csv';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      if (type === 'names') this._handleNameCsvUpload(file);
      else if (type === 'addresses') this._handleAddressCsvUpload(file);
    };
    input.click();
  }

  _setWidgetState(type, state, data = {}) {
    const selector = type === 'addresses' ? '.pzy-csv-widget-addresses' : '.pzy-csv-widget-names';
    const widget = document.querySelector(selector);
    if (!widget) return;
    widget.dataset.state = state;
    if (state === 'success') {
      const fn = widget.querySelector('.pzy-csv-filename');
      if (fn) fn.textContent = data.filename || '';
    } else if (state === 'error') {
      const msg = widget.querySelector('.pzy-csv-error-message');
      if (msg) msg.textContent = data.message || '';
    }
  }

  // ── Name CSV parsing ──

  _handleNameCsvUpload(file) {
    if (!window.Papa) {
      this._setWidgetState('names', 'error', { message: this.strings.csvErrorLibraryFailed || 'CSV parsing library failed to load. Please refresh.' });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      this._setWidgetState('names', 'error', { message: this.strings.csvErrorFileTooLarge || 'File is too large. Maximum file size is 2MB.' });
      return;
    }
    this._setWidgetState('names', 'uploading');

    window.Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        this.previewIndex = 0;
        const { names, error } = this._processAndValidateNames(results.data);
        if (error) {
          this.csvNames = [];
          this._setWidgetState('names', 'error', { message: error });
        } else {
          this.csvNames = names;
          this._setWidgetState('names', 'success', { filename: file.name });
        }
        this._renderNamePreview();
        this._updateAddToCartState();
      },
      error: () => {
        this._setWidgetState('names', 'error', { message: this.strings.csvErrorParseFailed || 'Could not parse the CSV file.' });
        this._updateAddToCartState();
      }
    });
  }

  _processAndValidateNames(data) {
    if (!data || data.length === 0) {
      return { names: [], error: this.strings.csvErrorEmpty || 'The uploaded CSV file is empty.' };
    }
    if (data.length > 1000) {
      return { names: [], error: (this.strings.csvErrorTooManyRows || 'Too many rows ({count} recipients). Maximum is 1,000.').replace('{count}', data.length) };
    }

    const headers = Object.keys(data[0]).map(h => h.toLowerCase().replace(/ /g, '_'));
    const addressColumns = ['address_line_1', 'city_name', 'state_or_province', 'zip_or_postal_code', 'country_code'];
    if (addressColumns.some(col => headers.includes(col))) {
      return { names: [], error: this.strings.csvErrorWrongTemplate || 'This looks like an addresses template. Please use the names-only template.' };
    }

    const firstNameKey = Object.keys(data[0]).find(k => k.toLowerCase().replace(/ /g, '_') === 'first_name');
    const lastNameKey = Object.keys(data[0]).find(k => k.toLowerCase().replace(/ /g, '_') === 'last_name');
    if (!firstNameKey || !lastNameKey) {
      return { names: [], error: this.strings.csvErrorInvalidHeadersNames || "CSV headers must include 'first_name' and 'last_name'." };
    }

    const sanitize = (str) => (typeof str !== 'string') ? '' : str.trim().replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const exampleNames = [{ first: 'albert', last: 'einstein' }, { first: 'marie', last: 'curie' }];
    const processedNames = [];
    const errors = [];

    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      const rowNum = i + 2;
      const rawFirst = row[firstNameKey];
      const rawLast = row[lastNameKey];

      if (typeof rawFirst === 'string' && rawFirst.includes('\n')) {
        errors.push((this.strings.csvErrorMultilineName || 'Row {row}: Multi-line values detected in {field}.').replace('{row}', rowNum).replace('{field}', 'first_name'));
        continue;
      }
      if (typeof rawLast === 'string' && rawLast.includes('\n')) {
        errors.push((this.strings.csvErrorMultilineName || 'Row {row}: Multi-line values detected in {field}.').replace('{row}', rowNum).replace('{field}', 'last_name'));
        continue;
      }

      const firstName = sanitize(rawFirst);
      const lastName = sanitize(rawLast);

      if (firstName.length > 35) { errors.push(`Row ${rowNum}: First name exceeds 35 characters.`); continue; }
      if (lastName.length > 35) { errors.push(`Row ${rowNum}: Last name exceeds 35 characters.`); continue; }
      if (firstName && !this.NAME_PATTERN.test(firstName)) { errors.push(`Row ${rowNum}: First name contains invalid characters.`); continue; }
      if (lastName && !this.NAME_PATTERN.test(lastName)) { errors.push(`Row ${rowNum}: Last name contains invalid characters.`); continue; }

      const isExample = exampleNames.some(ex => firstName.toLowerCase() === ex.first && lastName.toLowerCase() === ex.last);
      if ((firstName || lastName) && !isExample) {
        processedNames.push({ firstName, lastName });
      }
    }

    if (errors.length > 0) {
      let msg = errors.slice(0, 3).join('\n');
      if (errors.length > 3) msg += '\n' + (this.strings.csvErrorMoreErrors || '...and {count} more error{plural} found.').replace('{count}', errors.length - 3).replace('{plural}', errors.length - 3 > 1 ? 's' : '');
      return { names: [], error: msg };
    }
    if (processedNames.length === 0) {
      return { names: [], error: this.strings.csvErrorBlankFile || 'The file is blank or only contains example names.' };
    }
    return { names: processedNames, error: null };
  }

  _resetNameUpload() {
    this.csvNames = [];
    this.previewIndex = 0;
    this._renderNamePreview();
    this._setWidgetState('names', 'empty');
    this._updateAddToCartState();
  }

  // ── Address CSV parsing ──

  _handleAddressCsvUpload(file) {
    if (!window.Papa) {
      this._setWidgetState('addresses', 'error', { message: this.strings.csvErrorLibraryFailed || 'CSV parsing library failed to load. Please refresh.' });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      this._setWidgetState('addresses', 'error', { message: this.strings.csvErrorFileTooLarge || 'File is too large. Maximum file size is 2MB.' });
      return;
    }
    this._setWidgetState('addresses', 'uploading');

    window.Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        this.addressPreviewIndex = 0;
        const { addresses, error } = this._processAndValidateAddresses(results.data);
        if (error) {
          this.csvAddresses = [];
          this._setWidgetState('addresses', 'error', { message: error });
        } else {
          const combined = this._combineDuplicateAddresses(addresses);
          this.csvAddresses = combined;
          const total = combined.reduce((sum, a) => sum + a.quantity, 0);
          const unique = combined.length;
          let label = file.name;
          if (addresses.length > unique) label = `${file.name} (${unique} unique, ${total} total)`;
          this._setWidgetState('addresses', 'success', { filename: label });
        }
        this._renderAddressPreview();
        this._renderNonPersonalizedAddressPreview();
        this._updateAddToCartState();
      },
      error: () => {
        this._setWidgetState('addresses', 'error', { message: this.strings.csvErrorParseFailed || 'Could not parse the CSV file.' });
        this._updateAddToCartState();
      }
    });
  }

  _processAndValidateAddresses(data) {
    if (!data || data.length === 0) {
      return { addresses: [], error: this.strings.csvErrorEmpty || 'The uploaded CSV file is empty.' };
    }
    if (data.length > 1000) {
      return { addresses: [], error: (this.strings.csvErrorTooManyRows || 'Too many rows ({count} recipients). Maximum is 1,000.').replace('{count}', data.length) };
    }

    const sanitize = (str) => (typeof str !== 'string') ? '' : str.trim().replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const fieldMap = {};
    for (const key of Object.keys(data[0])) {
      fieldMap[key.toLowerCase().replace(/ /g, '_')] = key;
    }

    const requiredFields = ['first_name', 'last_name', 'optional_email', 'address_line_1', 'city_name', 'state_or_province', 'country_code', 'zip_or_postal_code'];
    for (const field of requiredFields) {
      if (!fieldMap[field]) {
        return { addresses: [], error: (this.strings.csvErrorMissingColumn || 'Missing required column: {field}').replace('{field}', field.replace(/_/g, ' ')) };
      }
    }

    const processed = [];
    const errors = [];

    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      const rowNum = i + 2;

      const rawFields = {
        first: row[fieldMap['first_name']], last: row[fieldMap['last_name']],
        email: row[fieldMap['optional_email']] || '', address1: row[fieldMap['address_line_1']],
        address2: row[fieldMap['address_line_2']] || '', city: row[fieldMap['city_name']],
        state: row[fieldMap['state_or_province']], zip: row[fieldMap['zip_or_postal_code']],
        country: row[fieldMap['country_code']]
      };

      const multilineFields = [];
      for (const [key, val] of Object.entries(rawFields)) {
        if (typeof val === 'string' && val.includes('\n')) multilineFields.push(key);
      }
      if (multilineFields.length > 0) {
        errors.push((this.strings.csvErrorMultiline || 'Row {row}: Multi-line values detected in {fields}.').replace('{row}', rowNum).replace('{fields}', multilineFields.join(', ')));
        continue;
      }

      const addr = {
        firstName: sanitize(rawFields.first), lastName: sanitize(rawFields.last),
        email: sanitize(rawFields.email), address1: sanitize(rawFields.address1),
        address2: sanitize(rawFields.address2), city: sanitize(rawFields.city),
        state: sanitize(rawFields.state).toUpperCase(), zip: sanitize(rawFields.zip),
        country: sanitize(rawFields.country).toUpperCase()
      };

      if (addr.firstName.toLowerCase() === 'albert' && addr.lastName.toLowerCase() === 'einstein' && addr.address1.toLowerCase() === '123 usa st.') continue;

      const rowErrors = this._validateSingleAddress(addr, rowNum);
      if (rowErrors.length > 0) { errors.push(...rowErrors); continue; }

      processed.push(addr);
    }

    if (processed.length === 0 && errors.length === 0) {
      return { addresses: [], error: this.strings.csvErrorTemplateOnly || 'Please add your actual recipient addresses.' };
    }
    if (errors.length > 0) {
      let msg = errors.slice(0, 3).join('\n');
      if (errors.length > 3) msg += '\n' + (this.strings.csvErrorMoreErrors || '...and {count} more error{plural} found.').replace('{count}', errors.length - 3).replace('{plural}', errors.length - 3 > 1 ? 's' : '');
      return { addresses: [], error: msg };
    }
    return { addresses: processed, error: null };
  }

  /** Validate a single address object. Returns array of error strings (empty = valid). */
  _validateSingleAddress(addr, rowNum) {
    const errs = [];
    const s = this.strings;

    if (!addr.firstName) errs.push(`Row ${rowNum}: First name is required.`);
    if (!addr.lastName) errs.push(`Row ${rowNum}: Last name is required.`);
    if (errs.length > 0) return errs;

    if (addr.firstName.length > 35) errs.push(`Row ${rowNum}: First name exceeds 35 characters.`);
    if (addr.lastName.length > 35) errs.push(`Row ${rowNum}: Last name exceeds 35 characters.`);
    if (addr.firstName && !this.NAME_PATTERN.test(addr.firstName)) errs.push(`Row ${rowNum}: First name contains invalid characters.`);
    if (addr.lastName && !this.NAME_PATTERN.test(addr.lastName)) errs.push(`Row ${rowNum}: Last name contains invalid characters.`);

    if (addr.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr.email)) errs.push(`Row ${rowNum}: Invalid email address.`);
    if (!addr.address1) errs.push(`Row ${rowNum}: Address is required.`);
    if (addr.address1 && addr.address1.length > 100) errs.push(`Row ${rowNum}: Address line 1 exceeds 100 characters.`);
    if (addr.address2 && addr.address2.length > 100) errs.push(`Row ${rowNum}: Address line 2 exceeds 100 characters.`);
    if (addr.address1 && /p\.?o\.?\s*box/i.test(addr.address1)) errs.push(`Row ${rowNum}: PO Box addresses are not supported.`);
    if (!addr.city) errs.push(`Row ${rowNum}: City is required.`);
    if (addr.city && addr.city.length > 50) errs.push(`Row ${rowNum}: City name exceeds 50 characters.`);
    if (addr.country && addr.country !== 'US') errs.push(`Row ${rowNum}: Country must be "US".`);
    if (!addr.state) errs.push(`Row ${rowNum}: State is required.`);
    else if (!this.VALID_STATES.has(addr.state)) errs.push(`Row ${rowNum}: Invalid state code "${addr.state}".`);
    if (!addr.zip) errs.push(`Row ${rowNum}: Zip code is required.`);
    else if (!/^\d{5}(-\d{4})?$/.test(addr.zip)) errs.push(`Row ${rowNum}: Invalid zip code "${addr.zip}".`);

    return errs;
  }

  _combineDuplicateAddresses(addresses) {
    const map = new Map();
    addresses.forEach(addr => {
      const key = `${addr.firstName}|${addr.lastName}|${addr.address1}|${addr.address2 || ''}|${addr.city}|${addr.state}|${addr.zip}`.toLowerCase();
      if (map.has(key)) map.get(key).quantity++;
      else map.set(key, { ...addr, quantity: 1 });
    });
    return Array.from(map.values());
  }

  _resetAddressUpload() {
    this.csvAddresses = [];
    this.addressPreviewIndex = 0;
    this.nonPersonalizedPreviewIndex = 0;
    this._renderAddressPreview();
    this._renderNonPersonalizedAddressPreview();
    this._setWidgetState('addresses', 'empty');
    this._updateAddToCartState();
  }

  // ── Manual address entry ──

  _initManualEntry() {
    const addBtn = document.getElementById('pzy-add-recipient-btn');
    const saveBtn = document.getElementById('pzy-addr-save');
    const cancelBtn = document.getElementById('pzy-addr-cancel');

    addBtn?.addEventListener('click', () => this._showAddressForm());
    saveBtn?.addEventListener('click', () => this._handleAddressSave());
    cancelBtn?.addEventListener('click', () => this._hideAddressForm());

    document.querySelectorAll('.pzy-recipient-tab[data-mode]').forEach(tab => {
      tab.addEventListener('click', () => {
        const mode = tab.dataset.mode;
        document.querySelectorAll('.pzy-recipient-tab[data-mode]').forEach(t => {
          t.classList.toggle('pzy-toggle-tab--active', t.dataset.mode === mode);
          t.setAttribute('aria-selected', t.dataset.mode === mode ? 'true' : 'false');
        });
        this._setRecipientMode(mode);
      });
    });
  }

  _setRecipientMode(mode) {
    this.recipientMode = mode;
    const csvMode = document.getElementById('pzy-mode-csv');
    const manualMode = document.getElementById('pzy-mode-manual');
    if (mode === 'manual') {
      if (csvMode) csvMode.style.display = 'none';
      if (manualMode) manualMode.style.display = '';
    } else {
      if (csvMode) csvMode.style.display = '';
      if (manualMode) manualMode.style.display = 'none';
      this._hideAddressForm();
    }
    this._renderAddressPreview();
    this._renderNonPersonalizedAddressPreview();
    this._updateAddToCartState();
  }

  _showAddressForm(addr = null, index = null) {
    const form = document.getElementById('pzy-address-form');
    const addBtn = document.getElementById('pzy-add-recipient-btn');
    const saveBtn = document.getElementById('pzy-addr-save');
    this.editingAddressIndex = Number.isInteger(index) ? index : null;
    if (form) form.style.display = '';
    if (addBtn) addBtn.style.display = 'none';
    if (saveBtn) saveBtn.textContent = this.editingAddressIndex !== null ? 'Update' : (this.strings.bulkSaveRecipient || 'Save');
    if (addr) this._populateAddressForm(addr);
    else this._clearAddressForm();
    document.getElementById('pzy-addr-first-name')?.focus();
  }

  _hideAddressForm() {
    const form = document.getElementById('pzy-address-form');
    const addBtn = document.getElementById('pzy-add-recipient-btn');
    const saveBtn = document.getElementById('pzy-addr-save');
    if (form) form.style.display = 'none';
    if (addBtn) addBtn.style.display = '';
    if (saveBtn) saveBtn.textContent = this.strings.bulkSaveRecipient || 'Save';
    this._clearAddressForm();
    this.editingAddressIndex = null;
  }

  _populateAddressForm(addr) {
    const values = {
      'pzy-addr-first-name': addr.firstName || '',
      'pzy-addr-last-name': addr.lastName || '',
      'pzy-addr-email': addr.email || '',
      'pzy-addr-address1': addr.address1 || '',
      'pzy-addr-address2': addr.address2 || '',
      'pzy-addr-city': addr.city || '',
      'pzy-addr-zip': addr.zip || ''
    };
    Object.entries(values).forEach(([id, value]) => {
      const el = document.getElementById(id);
      if (el) el.value = value;
    });
    const stateEl = document.getElementById('pzy-addr-state');
    if (stateEl) stateEl.value = addr.state || '';
    const errorEl = document.getElementById('pzy-address-form-error');
    if (errorEl) { errorEl.style.display = 'none'; errorEl.textContent = ''; }
  }

  _clearAddressForm() {
    ['pzy-addr-first-name','pzy-addr-last-name','pzy-addr-email','pzy-addr-address1','pzy-addr-address2','pzy-addr-city','pzy-addr-zip'].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.value = '';
    });
    const stateEl = document.getElementById('pzy-addr-state');
    if (stateEl) stateEl.value = '';
    const errorEl = document.getElementById('pzy-address-form-error');
    if (errorEl) { errorEl.style.display = 'none'; errorEl.textContent = ''; }
  }

  _handleAddressSave() {
    const addr = {
      firstName: (document.getElementById('pzy-addr-first-name')?.value || '').trim(),
      lastName: (document.getElementById('pzy-addr-last-name')?.value || '').trim(),
      email: (document.getElementById('pzy-addr-email')?.value || '').trim(),
      address1: (document.getElementById('pzy-addr-address1')?.value || '').trim(),
      address2: (document.getElementById('pzy-addr-address2')?.value || '').trim(),
      city: (document.getElementById('pzy-addr-city')?.value || '').trim(),
      state: (document.getElementById('pzy-addr-state')?.value || '').toUpperCase(),
      zip: (document.getElementById('pzy-addr-zip')?.value || '').trim(),
      country: 'US'
    };

    const errors = this._validateSingleAddress(addr, 0);
    const formattedErrors = errors.map(e => e.replace(/^Row 0: /, ''));
    const errorEl = document.getElementById('pzy-address-form-error');

    if (formattedErrors.length > 0) {
      if (errorEl) { errorEl.textContent = formattedErrors[0]; errorEl.style.display = ''; }
      return;
    }

    const existing = this.editingAddressIndex !== null ? this.manualAddresses[this.editingAddressIndex] : null;
    addr.quantity = existing?.quantity || 1;
    if (existing) {
      this.manualAddresses[this.editingAddressIndex] = addr;
    } else {
      this.manualAddresses.push(addr);
    }
    this._hideAddressForm();
    this._renderAddressList();
    this._renderNonPersonalizedAddressPreview();
    this._renderAddressPreview();
    this._updateAddToCartState();
    this.log(existing ? 'Manual address updated' : 'Manual address added, total:', this.manualAddresses.length);
  }

  _handleAddressEdit(index) {
    const addr = this.manualAddresses[index];
    if (!addr) return;
    this._showAddressForm(addr, index);
  }

  _handleAddressRemove(index) {
    const wasEditing = this.editingAddressIndex === index;
    this.manualAddresses.splice(index, 1);
    if (wasEditing) {
      this._hideAddressForm();
    } else if (this.editingAddressIndex !== null && index < this.editingAddressIndex) {
      this.editingAddressIndex -= 1;
    }
    this._renderAddressList();
    this._renderNonPersonalizedAddressPreview();
    this._renderAddressPreview();
    this._updateAddToCartState();
  }

  _renderAddressList() {
    const list = document.getElementById('pzy-address-list');
    if (!list) return;

    if (this.manualAddresses.length === 0) {
      list.innerHTML = '';
      return;
    }

    let html = `<div class="pzy-address-list-header">${this.manualAddresses.length} recipient${this.manualAddresses.length !== 1 ? 's' : ''} added</div>`;
    this.manualAddresses.forEach((addr, idx) => {
      const display = `${addr.firstName} ${addr.lastName} - ${addr.address1}, ${addr.city}, ${addr.state} ${addr.zip}`;
      html += `<div class="pzy-address-list-item" data-index="${idx}">
        <div class="pzy-address-list-item-text">${this._escapeHtml(display)}</div>
        <div class="pzy-address-list-actions">
          <button type="button" class="pzy-address-list-edit" data-edit-index="${idx}" aria-label="Edit recipient" tabindex="0">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
          </button>
          <button type="button" class="pzy-address-list-remove" data-remove-index="${idx}" aria-label="Remove recipient" tabindex="0">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>
      </div>`;
    });
    list.innerHTML = html;

    list.querySelectorAll('[data-edit-index]').forEach(btn => {
      btn.addEventListener('click', () => this._handleAddressEdit(parseInt(btn.dataset.editIndex, 10)));
    });
    list.querySelectorAll('[data-remove-index]').forEach(btn => {
      btn.addEventListener('click', () => this._handleAddressRemove(parseInt(btn.dataset.removeIndex, 10)));
    });
  }

  // ── Preview pagers ──

  _initPreviewPagers() {
    document.querySelector('.pzy-name-preview')?.addEventListener('click', (e) => {
      const btn = e.target.closest('.pzy-pager-btn');
      if (!btn || btn.disabled) return;
      this.previewIndex += parseInt(btn.dataset.direction, 10);
      this._renderNamePreview();
    });
    document.querySelector('.pzy-address-preview')?.addEventListener('click', (e) => {
      const btn = e.target.closest('.pzy-pager-btn');
      if (!btn || btn.disabled) return;
      this.addressPreviewIndex += parseInt(btn.dataset.direction, 10);
      this._renderAddressPreview();
    });
    document.querySelector('.pzy-non-personalized-address-preview')?.addEventListener('click', (e) => {
      const btn = e.target.closest('.pzy-pager-btn');
      if (!btn || btn.disabled) return;
      this.nonPersonalizedPreviewIndex += parseInt(btn.dataset.direction, 10);
      this._renderNonPersonalizedAddressPreview();
    });
  }


  // ── Manual name entry ──

  _initManualNames() {
    document.querySelectorAll('.pzy-names-tab').forEach(tab => {
      tab.addEventListener('click', () => {
        const mode = tab.dataset.namesMode;
        document.querySelectorAll('.pzy-names-tab').forEach(t => {
          t.classList.toggle('pzy-toggle-tab--active', t.dataset.namesMode === mode);
          t.setAttribute('aria-selected', t.dataset.namesMode === mode ? 'true' : 'false');
        });
        this._setNamesMode(mode);
      });
    });

    document.getElementById('pzy-add-name-btn')?.addEventListener('click', () => this._showNameForm());
    document.getElementById('pzy-name-save')?.addEventListener('click', () => this._handleNameSave());
    document.getElementById('pzy-name-cancel')?.addEventListener('click', () => this._hideNameForm());
  }

  _setNamesMode(mode) {
    this.namesMode = mode;
    const csvMode = document.getElementById('pzy-names-mode-csv');
    const manualMode = document.getElementById('pzy-names-mode-manual');
    if (mode === 'manual') {
      if (csvMode) csvMode.style.display = 'none';
      if (manualMode) manualMode.style.display = '';
    } else {
      if (csvMode) csvMode.style.display = '';
      if (manualMode) manualMode.style.display = 'none';
      this._hideNameForm();
    }
    this._renderNamePreview();
    this._updateAddToCartState();
  }

  _updateNameFieldVisibility() {
    const firstField = document.getElementById('pzy-name-field-first');
    const lastField = document.getElementById('pzy-name-field-last');
    const row = document.getElementById('pzy-name-fields');
    if (!firstField || !lastField) return;

    firstField.style.display = this.activeTokens.firstName ? '' : 'none';
    lastField.style.display = this.activeTokens.lastName ? '' : 'none';

    const bothVisible = this.activeTokens.firstName && this.activeTokens.lastName;
    if (row) row.classList.toggle('pzy-address-form-row--2col', bothVisible);
  }

  _showNameForm(name = null, index = null) {
    const form = document.getElementById('pzy-name-entry-form');
    const addBtn = document.getElementById('pzy-add-name-btn');
    const saveBtn = document.getElementById('pzy-name-save');
    this.editingNameIndex = Number.isInteger(index) ? index : null;
    this._updateNameFieldVisibility();
    if (form) form.style.display = '';
    if (addBtn) addBtn.style.display = 'none';
    if (saveBtn) saveBtn.textContent = this.editingNameIndex !== null ? 'Update' : 'Save';
    if (name) this._populateNameForm(name);
    else this._clearNameForm();
    const focusTarget = this.activeTokens.firstName
      ? document.getElementById('pzy-name-first')
      : document.getElementById('pzy-name-last');
    focusTarget?.focus();
  }

  _hideNameForm() {
    const form = document.getElementById('pzy-name-entry-form');
    const addBtn = document.getElementById('pzy-add-name-btn');
    const saveBtn = document.getElementById('pzy-name-save');
    if (form) form.style.display = 'none';
    if (addBtn) addBtn.style.display = '';
    if (saveBtn) saveBtn.textContent = 'Save';
    this._clearNameForm();
    this.editingNameIndex = null;
  }

  _clearNameForm() {
    const firstInput = document.getElementById('pzy-name-first');
    const lastInput = document.getElementById('pzy-name-last');
    if (firstInput) firstInput.value = '';
    if (lastInput) lastInput.value = '';
    const errEl = document.getElementById('pzy-name-entry-error');
    if (errEl) { errEl.style.display = 'none'; errEl.textContent = ''; }
  }

  _populateNameForm(name) {
    const firstInput = document.getElementById('pzy-name-first');
    const lastInput = document.getElementById('pzy-name-last');
    if (firstInput) firstInput.value = name.firstName || '';
    if (lastInput) lastInput.value = name.lastName || '';
    const errEl = document.getElementById('pzy-name-entry-error');
    if (errEl) { errEl.style.display = 'none'; errEl.textContent = ''; }
  }

  _handleNameSave() {
    const firstName = (document.getElementById('pzy-name-first')?.value || '').trim();
    const lastName = (document.getElementById('pzy-name-last')?.value || '').trim();
    const errEl = document.getElementById('pzy-name-entry-error');

    if (this.activeTokens.firstName && !firstName) {
      if (errEl) { errEl.textContent = 'First name is required.'; errEl.style.display = ''; }
      return;
    }
    if (this.activeTokens.lastName && !lastName) {
      if (errEl) { errEl.textContent = 'Last name is required.'; errEl.style.display = ''; }
      return;
    }
    if (firstName && !this.NAME_PATTERN.test(firstName)) {
      if (errEl) { errEl.textContent = 'First name contains invalid characters.'; errEl.style.display = ''; }
      return;
    }
    if (lastName && !this.NAME_PATTERN.test(lastName)) {
      if (errEl) { errEl.textContent = 'Last name contains invalid characters.'; errEl.style.display = ''; }
      return;
    }

    const name = { firstName, lastName };
    if (this.editingNameIndex !== null && this.manualNames[this.editingNameIndex]) {
      this.manualNames[this.editingNameIndex] = name;
    } else {
      this.manualNames.push(name);
    }
    this._hideNameForm();
    this._renderNamesList();
    this._renderNamePreview();
    this._updateAddToCartState();
  }

  _handleNameEdit(index) {
    const name = this.manualNames[index];
    if (!name) return;
    this._showNameForm(name, index);
  }

  _handleNameRemove(index) {
    const wasEditing = this.editingNameIndex === index;
    this.manualNames.splice(index, 1);
    if (wasEditing) {
      this._hideNameForm();
    } else if (this.editingNameIndex !== null && index < this.editingNameIndex) {
      this.editingNameIndex -= 1;
    }
    this._renderNamesList();
    this._renderNamePreview();
    this._updateAddToCartState();
  }

  _renderNamesList() {
    const list = document.getElementById('pzy-names-list');
    if (!list) return;
    if (this.manualNames.length === 0) { list.innerHTML = ''; return; }

    let html = `<div class="pzy-address-list-header">${this.manualNames.length} name${this.manualNames.length !== 1 ? 's' : ''} added</div>`;
    this.manualNames.forEach((name, idx) => {
      const display = [name.firstName, name.lastName].filter(Boolean).join(' ');
      html += `<div class="pzy-address-list-item" data-index="${idx}">
        <div class="pzy-address-list-item-text">${this._escapeHtml(display)}</div>
        <div class="pzy-address-list-actions">
          <button type="button" class="pzy-address-list-edit" data-edit-name-index="${idx}" aria-label="Edit name" tabindex="0">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
          </button>
          <button type="button" class="pzy-address-list-remove" data-remove-name-index="${idx}" aria-label="Remove name" tabindex="0">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>
      </div>`;
    });
    list.innerHTML = html;
    list.querySelectorAll('[data-edit-name-index]').forEach(btn => {
      btn.addEventListener('click', () => this._handleNameEdit(parseInt(btn.dataset.editNameIndex, 10)));
    });
    list.querySelectorAll('[data-remove-name-index]').forEach(btn => {
      btn.addEventListener('click', () => this._handleNameRemove(parseInt(btn.dataset.removeNameIndex, 10)));
    });
  }

  // ── Personalization detection ──

  detectPersonalization(noteText) {
    return /\[first_name\]|\[last_name\]/i.test(noteText || '');
  }

  /** Call when entering bulk-config stage to freeze personalization state from note text. */
  enterBulkConfig(noteText, hasCustomNote) {
    this.personalizationEnabled = hasCustomNote && this.detectPersonalization(noteText);

    this.activeTokens = {
      firstName: /\[first_name\]/i.test(noteText || ''),
      lastName: /\[last_name\]/i.test(noteText || '')
    };

    this._updateVisibility();
    this._updateAddToCartState();

    const hasPersonalizedNote = hasCustomNote && this.detectPersonalization(noteText);
    const reminder = document.querySelector('.pzy-personalization-reminder');
    if (reminder) reminder.style.display = hasPersonalizedNote ? 'none' : '';
  }

  // ── Name preview ──

  _renderNamePreview() {
    const container = document.querySelector('.pzy-name-preview');
    if (!container) return;
    const total = this.personalizedNames.length;
    if (total === 0) { container.style.display = 'none'; container.innerHTML = ''; return; }

    if (this.previewIndex < 0) this.previewIndex = 0;
    if (this.previewIndex >= total) this.previewIndex = total - 1;

    const noteText = document.getElementById('pzy-note-text')?.value || '';
    const person = this.personalizedNames[this.previewIndex];
    let note = noteText.replace(/\[first_name\]/gi, person.firstName || '').replace(/\[last_name\]/gi, person.lastName || '');

    container.innerHTML = `
      <div class="pzy-preview-header">
        <h6>${this.strings.notePreviewTitle || 'Puzzle box note preview:'}</h6>
        <div class="pzy-preview-pager">
          <span>${this.previewIndex + 1} / ${total}</span>
          <button class="pzy-pager-btn" data-direction="-1" aria-label="Previous name" ${this.previewIndex === 0 ? 'disabled' : ''} tabindex="0">&lsaquo;</button>
          <button class="pzy-pager-btn" data-direction="1" aria-label="Next name" ${this.previewIndex >= total - 1 ? 'disabled' : ''} tabindex="0">&rsaquo;</button>
        </div>
      </div>
      <div class="pzy-preview-item">${this._escapeHtml(note)}</div>`;
    container.style.display = '';
  }

  // ── Address preview (personalized) ──

  _renderAddressPreview() {
    const container = document.querySelector('.pzy-address-preview');
    if (!container) return;
    const total = this.recipientAddresses.length;
    const noteText = document.getElementById('pzy-note-text')?.value || '';
    const hasPersonalization = this.detectPersonalization(noteText);

    if (total === 0 || !hasPersonalization) { container.style.display = 'none'; container.innerHTML = ''; return; }

    if (this.addressPreviewIndex < 0) this.addressPreviewIndex = 0;
    if (this.addressPreviewIndex >= total) this.addressPreviewIndex = total - 1;

    const addr = this.recipientAddresses[this.addressPreviewIndex];
    let note = noteText.replace(/\[first_name\]/gi, addr.firstName || '').replace(/\[last_name\]/gi, addr.lastName || '');

    container.innerHTML = `
      <div class="pzy-preview-header">
        <h6>${this.strings.personalizedNotePreview || 'Personalized note preview:'}</h6>
        <div class="pzy-preview-pager">
          <span>${this.addressPreviewIndex + 1} / ${total}</span>
          <button class="pzy-pager-btn" data-direction="-1" aria-label="Previous recipient" ${this.addressPreviewIndex === 0 ? 'disabled' : ''} tabindex="0">&lsaquo;</button>
          <button class="pzy-pager-btn" data-direction="1" aria-label="Next recipient" ${this.addressPreviewIndex >= total - 1 ? 'disabled' : ''} tabindex="0">&rsaquo;</button>
        </div>
      </div>
      <div class="pzy-preview-item">${this._escapeHtml(note)}</div>`;
    container.style.display = '';
  }

  // ── Address preview (non-personalized) ──

  _renderNonPersonalizedAddressPreview() {
    const container = document.querySelector('.pzy-non-personalized-address-preview');
    if (!container) return;
    const total = this.recipientAddresses.length;
    const noteText = document.getElementById('pzy-note-text')?.value || '';
    const hasPersonalization = this.detectPersonalization(noteText);

    if (total === 0 || hasPersonalization) { container.style.display = 'none'; container.innerHTML = ''; return; }

    if (this.nonPersonalizedPreviewIndex < 0) this.nonPersonalizedPreviewIndex = 0;
    if (this.nonPersonalizedPreviewIndex >= total) this.nonPersonalizedPreviewIndex = total - 1;

    const addr = this.recipientAddresses[this.nonPersonalizedPreviewIndex];
    let text = `${addr.firstName || ''} ${addr.lastName || ''}`.trim();
    if (addr.address1) text += `\n${addr.address1}`;
    if (addr.address2) text += `\n${addr.address2}`;
    const cityStateZip = [addr.city, addr.state, addr.zip].filter(Boolean).join(', ');
    if (cityStateZip) text += `\n${cityStateZip}`;

    container.innerHTML = `
      <div class="pzy-preview-header">
        <h6>${this.strings.fileApprovedPreview || 'File approved. Preview:'}</h6>
        <div class="pzy-preview-pager">
          <span>${this.nonPersonalizedPreviewIndex + 1} / ${total}</span>
          <button class="pzy-pager-btn" data-direction="-1" aria-label="Previous recipient" ${this.nonPersonalizedPreviewIndex === 0 ? 'disabled' : ''} tabindex="0">&lsaquo;</button>
          <button class="pzy-pager-btn" data-direction="1" aria-label="Next recipient" ${this.nonPersonalizedPreviewIndex >= total - 1 ? 'disabled' : ''} tabindex="0">&rsaquo;</button>
        </div>
      </div>
      <div class="pzy-preview-item pzy-preview-item--address">${this._escapeHtml(text)}</div>`;
    container.style.display = '';
  }

  // ── Enhanced CSV generation ──

  generateEnhancedCsv(recipients, orderType, fileUrls, metadata) {
    const headers = [
      'first_name', 'last_name', 'optional_email',
      'address_line_1', 'address_line_2', 'city_name', 'state_or_province', 'country_code', 'zip_or_postal_code',
      'quantity', 'order_type', 'puzzle_size', 'orientation', 'variant_id',
      'custom_note_text', 'has_custom_image', 'has_custom_logo',
      'puzzle_print_url', 'box_cover_url', 'preview_image_url',
      'suppress_emails'
    ];

    const rows = recipients.map(recipient => {
      let noteText = metadata.customNote || '';
      if (orderType.includes('personalized')) {
        noteText = noteText.replace(/\[first_name\]/gi, recipient.firstName || '').replace(/\[last_name\]/gi, recipient.lastName || '');
      }
      noteText = noteText.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, '');

      return [
        recipient.firstName || '', recipient.lastName || '', recipient.email || '',
        recipient.address1 || '', recipient.address2 || '', recipient.city || '',
        recipient.state || '', recipient.country || '', recipient.zip || '',
        recipient.quantity || 1, orderType, metadata.size, metadata.orientation, metadata.variantId,
        noteText, metadata.hasCustomImage ? 'true' : 'false', metadata.hasCustomLogo ? 'true' : 'false',
        fileUrls.puzzlePrint || '', fileUrls.boxCover || '', fileUrls.preview || '',
        metadata.suppressEmails ? 'true' : 'false'
      ];
    });

    const csvContent = [headers, ...rows].map(row =>
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    if (rows.length === 0) throw new Error('Cannot create order with 0 recipients');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    return new File([blob], `enhanced-order-${Date.now()}.csv`, { type: 'text/csv' });
  }

  // ── Order type determination ──

  getOrderType(noteText, hasCustomNote) {
    const shipping = this.getSelectedShipping();
    const hasPersonalization = hasCustomNote && this.detectPersonalization(noteText);

    if (shipping === 'single' && !hasPersonalization) return 'single_address';
    if (shipping === 'single' && hasPersonalization) return 'single_address_personalized';
    if (shipping === 'multiple' && !hasPersonalization) return 'multi_recipient';
    if (shipping === 'multiple' && hasPersonalization) return 'multi_recipient_personalized';
    return null;
  }

  /** Get the total quantity for the current order configuration. */
  getTotalQuantity(orderType) {
    if (orderType === 'single_address') return this.quantity || 1;
    if (orderType === 'single_address_personalized') return this.personalizedNames.length;
    if (orderType?.startsWith('multi_recipient')) {
      return this.recipientAddresses.reduce((sum, a) => sum + (a.quantity || 1), 0);
    }
    return 1;
  }

  /** Get the recipients array for enhanced CSV generation. */
  getRecipients(orderType) {
    if (orderType === 'single_address') return [{ firstName: '', lastName: '', quantity: this.quantity || 1 }];
    if (orderType === 'single_address_personalized') return this.personalizedNames;
    if (orderType?.startsWith('multi_recipient')) return this.recipientAddresses;
    return [];
  }

  // ── Full reset ──

  reset() {
    this.csvNames = [];
    this.manualNames = [];
    this.namesMode = 'csv';
    this.csvAddresses = [];
    this.manualAddresses = [];
    this.recipientMode = 'csv';
    this.activeTokens = { firstName: false, lastName: false };
    this.quantity = 1;
    this.previewIndex = 0;
    this.addressPreviewIndex = 0;
    this.nonPersonalizedPreviewIndex = 0;
    this.personalizationEnabled = false;
    this.editingAddressIndex = null;
    this.editingNameIndex = null;

    const qInput = document.getElementById('pzy-bulk-quantity-input');
    if (qInput) qInput.value = 1;

    this._resetNameUpload();
    this._resetAddressUpload();

    document.querySelectorAll('.pzy-bulk-shipping-card').forEach(card => {
      card.classList.remove('is-selected');
      card.setAttribute('aria-checked', 'false');
    });

    const suppress = document.getElementById('pzy-suppress-emails');
    if (suppress) suppress.checked = false;

    this._setRecipientMode('csv');
    this._setNamesMode('csv');
    document.querySelectorAll('.pzy-recipient-tab:not(.pzy-names-tab)').forEach(t => {
      t.classList.toggle('pzy-toggle-tab--active', t.dataset.mode === 'csv');
      t.setAttribute('aria-selected', t.dataset.mode === 'csv' ? 'true' : 'false');
    });
    document.querySelectorAll('.pzy-names-tab').forEach(t => {
      t.classList.toggle('pzy-toggle-tab--active', t.dataset.namesMode === 'csv');
      t.setAttribute('aria-selected', t.dataset.namesMode === 'csv' ? 'true' : 'false');
    });

    this._hideAddressForm();
    this._hideNameForm();
    this._renderAddressList();
    this._renderNamesList();

    const reminder = document.querySelector('.pzy-personalization-reminder');
    if (reminder) reminder.style.display = 'none';

    const btn = document.getElementById('pzy-bulk-add-to-cart');
    if (btn) btn.disabled = true;
  }

  _escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML.replace(/\n/g, '<br>');
  }
};

/* ========================================================================
   Puzzle Customizer
   ======================================================================== */

const PuzzleCustomizer = class {
  constructor() {
    this.config = window.PZY_CONFIG || {};
    this.debug = this.config.debug || false;
    this.strings = this.config.strings || {};
    this.isBulk = this.config.isBulk || false;

    // --- State ---
    this.selectedPieceCount = null;
    this.quantity = 1;
    this.isModalOpen = false;
    this.savedScrollY = 0;
    this.hasCustomNote = false;
    this.hasCustomImage = false;
    this.hasCustomLogo = false;

    // --- Image pipeline state ---
    this.originalFile = null;
    this.uploadedImageUrl = null;
    this.loadingTimeout = null;

    /** @type {number} Maximum accepted source file size (20 MB) */
    this.MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

    /** @type {number} iOS Safari caps canvas at ~16MP */
    this.MAX_MEGAPIXELS = 16;

    /** @type {Map<string, HTMLImageElement>} Cache for loaded CDN images */
    this.imageCache = new Map();

    // --- Cropper state ---
    /** @type {Cropper|null} */
    this.cropper = null;
    this.isRotated = false;
    this.croppedOrientation = false;
    this.croppedImageCanvas = null;
    this.minZoom = 1;
    this.maxZoom = 3;
    this.initialZoom = 1;

    // --- DOM refs ---
    this.customizer = document.getElementById('pzy-customizer');
    this.modalOverlay = document.getElementById('pzy-modal-overlay');
    this.modalTitle = document.getElementById('pzy-modal-title');
    this.backBtn = document.getElementById('pzy-modal-back');
    this.closeBtn = document.getElementById('pzy-modal-close');
    this.triggerBtn = document.getElementById('pzy-customize-trigger');
    this.fileInput = document.getElementById('pzy-file-input');
    this.uploadArea = document.getElementById('pzy-upload-area');
    this.uploadBtn = document.getElementById('pzy-upload-btn');
    this.pieceButtons = document.querySelectorAll('.pzy-piece-btn');
    this.noteCheckbox = document.getElementById('pzy-custom-note');
    this.imageCheckbox = document.getElementById('pzy-custom-image');
    this.logoCheckbox = document.getElementById('pzy-custom-logo');
    this.noteContent = document.getElementById('pzy-note-content');
    this.imageContent = document.getElementById('pzy-image-content');
    this.logoContent = document.getElementById('pzy-logo-content');
    this.noteTextArea = document.getElementById('pzy-note-text');
    this.noteRow1Count = document.getElementById('pzy-note-row1-count');
    this.noteRow2Count = document.getElementById('pzy-note-row2-count');
    this.rightsCheckbox = document.getElementById('pzy-rights-checkbox');
    this.addToCartBtn = document.getElementById('pzy-add-to-cart');
    this.quantityInput = document.getElementById('pzy-quantity-input');
    this.continueShoppingBtn = document.getElementById('pzy-continue-shopping');
    this.goToCartBtn = document.getElementById('pzy-go-to-cart');
    this.cropImage = document.getElementById('pzy-crop-image');
    this.previewImage = document.getElementById('pzy-preview-image');
    this.zoomSlider = document.getElementById('pzy-zoom-slider');
    this.loadingOverlay = document.getElementById('pzy-loading-overlay');
    this.loadingText = this.loadingOverlay?.querySelector('.pzy-loading-text');
    this.viewToggle = document.getElementById('pzy-view-toggle');
    this.currentView = 'box';
    this.customImageInput = document.querySelector('.pzy-custom-image-input');
    this.customLogoInput = document.querySelector('.pzy-custom-logo-input');
    this.refImageCheckbox = document.getElementById('pzy-use-custom-for-reference');
    this.customImageBlob = null;
    this.customLogoBlob = null;
    this.useCustomForReference = true;
    this.zoomToggle = document.getElementById('pzy-zoom-toggle');
    this.fullscreenPreview = document.getElementById('pzy-fullscreen-preview');
    this.fullscreenImage = document.getElementById('pzy-fullscreen-image');
    this.fullscreenClose = document.getElementById('pzy-fullscreen-close');
    this.rotateBtn = document.getElementById('pzy-rotate-btn');

    // --- Stage titles ---
    this.stageTitles = {
      upload: this.strings.upload || 'Upload your photo',
      crop: this.strings.crop || 'Crop your photo',
      preview: this.strings.preview || 'Preview',
      'custom-crop': this.strings.cropBoxImage || 'Adjust crop',
      processing: this.strings.processing || 'Processing...',
      success: this.strings.success || 'Success!',
      'bulk-config': this.strings.bulkConfig || 'Bulk ordering',
    };

    // --- Stage manager ---
    const stages = ['upload', 'crop', 'custom-crop', 'preview', 'processing', 'success'];
    if (this.isBulk) {
      stages.splice(stages.indexOf('processing'), 0, 'bulk-config');
    }

    this.stageManager = new StageManager(stages, {
      onTransition: (from, to) => this.handleStageTransition(from, to),
      log: (...args) => this.log(...args),
    });
    this.stageManager.disableStage('custom-crop');

    this.bulkManager = this.isBulk ? new BulkManager(this.config, { log: (...args) => this.log(...args) }) : null;
    this.bulkAddToCartBtn = document.getElementById('pzy-bulk-add-to-cart');

    this.hoistModalsToBody();
    this.bindEvents();
    this.renderPrices();
    this.autoSelectPieceCount();
    this.log('PuzzleCustomizer initialized', { debug: this.debug, isBulk: this.isBulk });
  }

  /** Pre-select a piece count from the URL variant param, or default to 250. */
  autoSelectPieceCount() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    let targetPieces = 250;

    if (variantId) {
      const productType = this.isBulk ? 'bulk' : 'standard';
      const allVariants = this.config.variants?.[productType] || {};
      for (const [pieces, combos] of Object.entries(allVariants)) {
        for (const combo of Object.values(combos)) {
          if (String(combo.id) === String(variantId)) {
            targetPieces = parseInt(pieces, 10);
            break;
          }
        }
      }
    }

    const btn = document.querySelector(`.pzy-piece-btn[data-pieces="${targetPieces}"]`);
    if (btn) this.handlePieceCountSelect(btn);
  }

  // --- Logging ---

  log(...args) {
    if (!this.debug) return;
    console.log('[PZY]', ...args);
  }

  // --- Currency formatting ---

  /**
   * Format a price in cents using the shop's money_format.
   * Falls back to simple USD formatting if money_format is missing.
   * @param {number} cents
   * @returns {string}
   */
  formatMoney(cents) {
    const format = this.config.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const amountNoDecimals = Math.floor(cents / 100).toString();
    const amountWithComma = amount.replace('.', ',');

    return format
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount}}', amount);
  }

  /** Format with currency code (e.g., "$3.00 USD"). Used for add-on price labels. */
  formatMoneyWithCurrency(cents) {
    const format = this.config.moneyFormatWithCurrency || this.config.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const amountNoDecimals = Math.floor(cents / 100).toString();
    const amountWithComma = amount.replace('.', ',');

    return format
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount}}', amount);
  }

  /**
   * Look up a variant's live price from the Shopify-injected price map.
   * @param {number} variantId
   * @returns {number|null} price in cents
   */
  getVariantPrice(variantId) {
    return this.config.variantPrices?.[variantId] ?? null;
  }

  /** Render prices on piece count buttons and add-on labels using live variant data. */
  renderPrices() {
    const productType = this.isBulk ? 'bulk' : 'standard';
    const allVariants = this.config.variants?.[productType] || {};

    document.querySelectorAll('[data-piece-price]').forEach(el => {
      const pieces = parseInt(el.dataset.piecePrice, 10);
      const baseVariantId = allVariants[pieces]?.none?.id;
      if (!baseVariantId) return;
      const price = this.getVariantPrice(baseVariantId);
      if (price != null) el.textContent = this.formatMoney(price);
    });

    this.updateAddonPrices();
  }

  /** Update add-on price labels, respecting upsell modifier (FREE overrides). */
  updateAddonPrices() {
    const productType = this.isBulk ? 'bulk' : 'standard';
    const sizeVariants = this.config.variants?.[productType]?.[this.selectedPieceCount || 250];
    if (!sizeVariants) return;

    const basePrice = this.getVariantPrice(sizeVariants.none?.id);
    if (basePrice == null) return;

    const activeModifier = this.config.customerUpsellModifier || this.config.storewideUpsellModifier;

    const freeRules = {
      note: activeModifier === 'Free custom notes' || activeModifier === 'Free upsells',
      image: activeModifier === 'Free custom images' || activeModifier === 'Free upsells',
      logo: activeModifier === 'Free custom logos' || activeModifier === 'Free upsells',
    };

    document.querySelectorAll('[data-addon]').forEach(el => {
      const addon = el.dataset.addon;
      const addonVariantId = sizeVariants[addon]?.id;
      if (!addonVariantId) return;
      const addonPrice = this.getVariantPrice(addonVariantId);
      if (addonPrice == null) return;
      const delta = addonPrice - basePrice;

      if (freeRules[addon]) {
        el.innerHTML = delta > 0
          ? `<s style="opacity:0.5">(+ ${this.formatMoneyWithCurrency(delta)})</s> <strong style="color:#3d6566">FREE</strong>`
          : '<strong style="color:#3d6566">FREE</strong>';
      } else {
        el.textContent = delta > 0 ? `(+ ${this.formatMoneyWithCurrency(delta)})` : '';
      }
    });
  }

  /**
   * Move modal overlays to <body> so they escape any parent stacking contexts
   * created by transforms, filters, etc. in the theme's section layout.
   */
  hoistModalsToBody() {
    [this.modalOverlay, this.fullscreenPreview].forEach(el => {
      if (el && el.parentElement !== document.body) {
        document.body.appendChild(el);
      }
    });
  }

  // --- Event binding ---

  bindEvents() {
    this.pieceButtons.forEach(btn => {
      btn.addEventListener('click', () => this.handlePieceCountSelect(btn));
      btn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.handlePieceCountSelect(btn);
        }
      });
    });

    this.triggerBtn?.addEventListener('click', () => this.handleOpenModal());
    this.closeBtn?.addEventListener('click', () => this.handleCloseModal());
    this.backBtn?.addEventListener('click', () => this.handleBack());

    this.uploadBtn?.addEventListener('click', () => this.fileInput?.click());
    this.uploadArea?.addEventListener('click', () => this.fileInput?.click());
    this.uploadArea?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.fileInput?.click();
      }
    });

    this.fileInput?.addEventListener('change', (e) => this.handleFileSelect(e));

    // Drag and drop
    if (this.uploadArea) {
      this.uploadArea.addEventListener('dragover', (e) => { e.preventDefault(); this.uploadArea.classList.add('pzy-drag-over'); });
      this.uploadArea.addEventListener('dragleave', () => { this.uploadArea.classList.remove('pzy-drag-over'); });
      this.uploadArea.addEventListener('drop', (e) => {
        e.preventDefault();
        this.uploadArea.classList.remove('pzy-drag-over');
        const file = e.dataTransfer?.files?.[0];
        if (file) {
          const dt = new DataTransfer();
          dt.items.add(file);
          this.fileInput.files = dt.files;
          this.fileInput.dispatchEvent(new Event('change'));
        }
      });
    }

    this.modalOverlay?.addEventListener('mousedown', (e) => {
      this._overlayMouseDownTarget = e.target;
    });
    this.modalOverlay?.addEventListener('mouseup', (e) => {
      if (e.target === this.modalOverlay && this._overlayMouseDownTarget === this.modalOverlay) {
        this.handleCloseModal();
      }
      this._overlayMouseDownTarget = null;
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isModalOpen) this.handleCloseModal();
    });

    // Option toggles — bind on the full header row, not just the checkbox
    this.noteCheckbox?.addEventListener('change', () => this.handleOptionToggle('note'));
    this.imageCheckbox?.addEventListener('change', () => this.handleOptionToggle('image'));
    this.logoCheckbox?.addEventListener('change', () => this.handleOptionToggle('logo'));
    document.querySelectorAll('.pzy-option-header').forEach(header => {
      header.addEventListener('click', (e) => {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'LABEL' || e.target.closest('label')) return;
        const checkbox = header.querySelector('.pzy-checkbox');
        if (checkbox) checkbox.click();
      });
    });

    // Note: 2-line limit + character count
    this.noteTextArea?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && this.noteTextArea.value.split('\n').length >= 2) {
        e.preventDefault();
      }
    });
    this.noteTextArea?.addEventListener('input', () => this.handleNoteInput());

    // Rights checkbox → enable/disable add-to-cart
    this.rightsCheckbox?.addEventListener('change', () => this.handleRightsChange());

    // Quantity buttons
    document.querySelector('.pzy-quantity-minus')?.addEventListener('click', () => this.handleQuantityChange(-1));
    document.querySelector('.pzy-quantity-plus')?.addEventListener('click', () => this.handleQuantityChange(1));
    this.quantityInput?.addEventListener('change', () => this.handleQuantityInput());

    // Add to cart
    this.addToCartBtn?.addEventListener('click', () => this.handleAddToCart());

    // Success stage buttons
    this.continueShoppingBtn?.addEventListener('click', () => this.handleContinueShopping());
    this.goToCartBtn?.addEventListener('click', () => { window.location.href = '/cart'; });

    // Fullscreen preview
    this.zoomToggle?.addEventListener('click', () => this.handleFullscreenOpen());
    this.fullscreenClose?.addEventListener('click', () => this.handleFullscreenClose());
    this.fullscreenPreview?.addEventListener('click', () => this.handleFullscreenClose());

    // View toggle (Box / Puzzle)
    this.viewToggle?.querySelectorAll('.pzy-view-tab').forEach(tab => {
      tab.addEventListener('click', () => this.handleViewToggle(tab.dataset.view));
    });

    // Custom image/logo upload buttons (multiple elements share these classes)
    document.querySelectorAll('.pzy-custom-image-upload').forEach(btn => {
      btn.addEventListener('click', () => this.customImageInput?.click());
    });
    document.querySelectorAll('.pzy-custom-logo-upload').forEach(btn => {
      btn.addEventListener('click', () => this.customLogoInput?.click());
    });
    this.customImageInput?.addEventListener('change', (e) => this.handleCustomUpload(e, 'image'));
    this.customLogoInput?.addEventListener('change', (e) => this.handleCustomUpload(e, 'logo'));

    // Custom image/logo remove buttons
    document.querySelector('.pzy-custom-image-remove')?.addEventListener('click', () => this.handleCustomRemove('image'));
    document.querySelector('.pzy-custom-logo-remove')?.addEventListener('click', () => this.handleCustomRemove('logo'));

    // Reference image checkbox
    this.refImageCheckbox?.addEventListener('change', () => {
      this.useCustomForReference = this.refImageCheckbox.checked;
      this.log('Reference image toggle:', this.useCustomForReference);
    });

    // Zoom sliders
    this.zoomSlider?.addEventListener('input', (e) => this.handleZoom(parseInt(e.target.value, 10)));
    document.getElementById('pzy-custom-zoom-slider')?.addEventListener('input', (e) => {
      if (!this.customCropper) return;
      const containerData = this.customCropper.getContainerData();
      const imageData = this.customCropper.getImageData();
      const minZ = Math.min(containerData.width / imageData.naturalWidth, containerData.height / imageData.naturalHeight);
      const maxZ = minZ * 3;
      const val = minZ + (parseInt(e.target.value, 10) / 100) * (maxZ - minZ);
      if (!isNaN(val)) this.customCropper.zoomTo(val);
    });

    // Rotate button
    this.rotateBtn?.addEventListener('click', () => this.handleRotation());

    // Crop footer next buttons
    document.querySelectorAll('.pzy-next-btn').forEach(btn => {
      btn.addEventListener('click', () => this.handleNextFromStage());
    });

    // Bulk-specific bindings
    if (this.bulkManager) {
      this.bulkManager.init();
      this.bulkAddToCartBtn?.addEventListener('click', () => this.handleBulkAddToCart());
    }
  }

  // --- Piece count selection ---

  handlePieceCountSelect(btn) {
    const pieces = parseInt(btn.dataset.pieces, 10);
    this.selectedPieceCount = pieces;

    this.pieceButtons.forEach(b => b.classList.remove('pzy-piece-btn--active'));
    btn.classList.add('pzy-piece-btn--active');

    if (this.triggerBtn) this.triggerBtn.disabled = false;

    this.updateAddonPrices();
    this.log('Piece count selected:', pieces);
  }

  // --- Modal open/close ---

  handleOpenModal() {
    if (!this.selectedPieceCount) return;

    this.resetAllState();
    this.isModalOpen = true;

    this.savedScrollY = window.scrollY;
    document.body.classList.add('pzy-modal-open');
    document.body.style.top = `-${this.savedScrollY}px`;

    this.modalOverlay?.classList.add('active');
    this.stageManager.goTo('upload');

    this.log('Modal opened, piece count:', this.selectedPieceCount);
  }

  handleCloseModal() {
    if (this.stageManager.current() === 'processing') return;

    this.isModalOpen = false;
    this.modalOverlay?.classList.remove('active');

    document.body.classList.remove('pzy-modal-open');
    document.body.style.top = '';
    window.scrollTo(0, this.savedScrollY);

    this.log('Modal closed');
  }

  // --- Navigation ---

  async handleBack() {
    const current = this.stageManager.current();
    if (current === 'upload') {
      this.handleCloseModal();
      return;
    }
    if (current === 'custom-crop') {
      this.stageManager.goTo('preview');
      return;
    }
    if (current === 'bulk-config') {
      this.bulkManager?.reset();
      this.stageManager.goTo('preview');
      return;
    }
    if (current === 'preview') {
      this.resetCustomizations();
      this.stageManager.goTo('crop');
      await this.initCropper();
      return;
    }
    this.stageManager.goBack();
  }

  /** Validate that checked customization options have their required uploads attached. */
  validateCustomizationUploads() {
    if (this.hasCustomImage && !this.customImageBlob) {
      this.showError('Please upload a custom image, or uncheck the custom image option.');
      return false;
    }
    if (this.hasCustomLogo && !this.customLogoBlob) {
      this.showError('Please upload a custom logo, or uncheck the custom logo option.');
      return false;
    }
    return true;
  }

  handleNextFromStage() {
    const current = this.stageManager.current();
    this.log('Next from stage:', current);

    switch (current) {
      case 'crop':
        this.handleCrop();
        return;
      case 'preview':
        if (this.isBulk && !this.rightsCheckbox?.checked) {
          this.syncRightsGateState();
          return;
        }
        if (!this.validateCustomizationUploads()) return;
        if (this.isBulk) {
          this.stageManager.goTo('bulk-config');
        }
        return;
      case 'custom-crop':
        this.handleCustomCropConfirm();
        return;
      default:
        this.stageManager.goForward();
    }
  }

  // --- Stage transitions ---

  handleStageTransition(from, to) {
    document.querySelectorAll('.pzy-stage').forEach(el => { el.style.display = 'none'; });
    document.querySelectorAll('.pzy-footer-stage').forEach(el => { el.style.display = 'none'; });

    const stage = document.querySelector(`.pzy-stage[data-stage="${to}"]`);
    const footer = document.querySelector(`.pzy-footer-stage[data-stage="${to}"]`);
    if (stage) stage.style.display = '';
    if (footer) footer.style.display = '';

    if (this.modalTitle) {
      this.modalTitle.textContent = this.stageTitles[to] || '';
    }

    const hideBackOn = ['upload', 'processing', 'success'];
    if (this.backBtn) {
      this.backBtn.style.visibility = hideBackOn.includes(to) ? 'hidden' : 'visible';
    }

    if (this.closeBtn) {
      this.closeBtn.style.visibility = to === 'processing' ? 'hidden' : 'visible';
    }

    if (to === 'preview') {
      this.syncRightsGateState();
    }

    if (to === 'success') {
      this.updateDiscountMessage();
      const msgEl = document.getElementById('pzy-success-message');
      if (msgEl) {
        const qty = this.isBulk && this.bulkManager
          ? this.bulkManager.getTotalQuantity(this.bulkManager.getOrderType(this.noteTextArea?.value || '', this.hasCustomNote))
          : this.quantity;
        msgEl.textContent = qty > 1
          ? 'Your custom puzzles have been added to your cart!'
          : 'Your custom puzzle has been added to your cart!';
      }
    }

    if (to === 'bulk-config' && this.bulkManager) {
      const noteChecked = this.noteCheckbox?.checked || false;
      const noteText = noteChecked ? (this.noteTextArea?.value || '') : '';
      this.bulkManager.enterBulkConfig(noteText, noteChecked);
    }
  }

  /** Update the discount note based on the current cart item count. */
  async updateDiscountMessage() {
    const noteEl = document.querySelector('.pzy-discount-note');
    if (!noteEl) return;

    try {
      const res = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      const count = cart.item_count || 0;

      const tiers = [
        { min: 1000, discount: 40 },
        { min: 500, discount: 30 },
        { min: 250, discount: 25 },
        { min: 100, discount: 20 },
        { min: 50, discount: 15 },
        { min: 25, discount: 12 },
        { min: 5, discount: 10 },
        { min: 3, discount: 8 },
        { min: 2, discount: 5 },
      ];

      const currentTier = tiers.find(t => count >= t.min);
      const nextTier = currentTier
        ? tiers[tiers.indexOf(currentTier) - 1]
        : tiers[tiers.length - 1];

      if (this.isBulk) {
        noteEl.innerHTML = `<strong>Bulk discounts applied at checkout</strong><span class="pzy-discount-detail">The more you order, the more you save</span>`;
      } else if (currentTier && !nextTier) {
        noteEl.innerHTML = `<strong>You're saving ${currentTier.discount}%!</strong><span class="pzy-discount-detail">Maximum discount applied</span>`;
      } else if (currentTier) {
        const needed = nextTier.min - count;
        noteEl.innerHTML = `<strong>You're saving ${currentTier.discount}%!</strong><span class="pzy-discount-detail">Add ${needed} more for ${nextTier.discount}% off</span>`;
      } else {
        noteEl.innerHTML = `<strong>More puzzles = greater discounts</strong><span class="pzy-discount-detail">Add 1 more for 5% off</span>`;
      }
    } catch (err) {
      this.log('Discount message update failed:', err.message);
    }
  }

  // --- Image pipeline (Phase 2) ---

  /**
   * Full upload flow: validate → HEIC convert → downscale → load → crop stage.
   * @param {Event} event - file input change event
   */
  async handleFileSelect(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    this.log('File selected:', file.name, file.type, `${(file.size / 1024).toFixed(1)} KB`);

    if (file.size > this.MAX_UPLOAD_BYTES) {
      this.showError('File size must be less than 20 MB. Please choose a smaller image.');
      return;
    }

    this.showLoading('Processing your photo...');

    try {
      let processedFile = file;

      const heicResult = await this.convertHeicIfNeeded(file, 'puzzle image');
      processedFile = heicResult.file;
      if (heicResult.wasConverted) {
        this.log('HEIC converted successfully');
      }

      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(processedFile.type)) {
        this.hideLoading();
        this.showError('Please upload a valid image file (JPEG, PNG, or WebP).');
        return;
      }

      processedFile = await this.downscaleIfNeeded(processedFile);

      this.originalFile = processedFile;

      if (this.uploadedImageUrl) {
        URL.revokeObjectURL(this.uploadedImageUrl);
      }
      this.uploadedImageUrl = URL.createObjectURL(processedFile);

      await this.loadImageWithTimeout(this.uploadedImageUrl, this.cropImage, 10000);

      this.stageManager.goTo('crop');
      await this.initCropper();

    } catch (error) {
      console.error('[PZY] Image processing error:', error);
      this.showError(error.message || 'Error processing your image. Please try a different file.');
    } finally {
      this.hideLoading();
    }
  }

  /**
   * Detect HEIC/HEIF and convert to JPEG.
   * Strategy: try native browser decode first (Safari supports HEIC), then heic2any fallback.
   * Returns original file unchanged if not HEIC.
   */
  async convertHeicIfNeeded(file, context = 'image') {
    const heicMimeTypes = ['image/heic', 'image/heif'];
    const isHeicByMime = heicMimeTypes.includes(file.type.toLowerCase());
    const isHeicByExtension = /\.(heic|heif)$/i.test(file.name);
    const isHeic = isHeicByMime || isHeicByExtension || (file.type === '' && isHeicByExtension);

    if (!isHeic) return { file, wasConverted: false };

    this.setLoadingText('Converting your photo...');
    this.log('HEIC detected, converting...', { name: file.name, type: file.type, size: file.size });

    // Strategy 1: native browser decode (Safari, and Chrome on some platforms)
    const nativeResult = await this.tryNativeHeicDecode(file);
    if (nativeResult) {
      this.log('HEIC converted via native browser decode', { newSize: nativeResult.size });
      return { file: nativeResult, wasConverted: true };
    }

    // Strategy 2: heic-to library (lazy-loaded on first HEIC upload)
    if (!window.HeicTo && !window.heicTo) {
      await this.loadHeicLibrary();
    }
    const heicToFn = window.HeicTo || window.heicTo;
    if (!heicToFn) {
      throw new Error(
        `Your browser doesn't support HEIC files directly. Please convert to JPEG or PNG first.`
      );
    }

    try {
      const convertedBlob = await heicToFn({ blob: file, type: 'image/jpeg', quality: 0.95 });
      const finalBlob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
      const newFileName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
      const convertedFile = new File([finalBlob], newFileName, { type: 'image/jpeg', lastModified: Date.now() });

      this.log('HEIC converted via heic-to', {
        newSize: convertedFile.size,
        ratio: ((1 - convertedFile.size / file.size) * 100).toFixed(1) + '% smaller',
      });

      return { file: convertedFile, wasConverted: true };
    } catch (error) {
      console.error('[PZY] heic-to conversion error:', error);
      this.log('heic-to failed', { errorMessage: error.message, fileName: file.name });

      if (error.message?.includes('not a valid HEIC') || error.message?.includes('corrupted')) {
        throw new Error(`The ${context} file appears corrupted. Please try a different file.`);
      }

      throw new Error(
        `We couldn't convert your iPhone photo automatically. ` +
        `Try one of these:\n\n` +
        `• On iPhone: Settings → Camera → Formats → Most Compatible\n` +
        `• Or: open the photo, take a screenshot, and upload that instead\n` +
        `• Or: email the photo to yourself (it auto-converts to JPEG)`
      );
    }
  }

  /**
   * Dynamically load heic-to library on first HEIC upload.
   * Cached — only injects the script once.
   */
  loadHeicLibrary() {
    if (!this._heicLoadPromise) {
      this._heicLoadPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/heic-to@1.4.2/dist/iife/heic-to.js';
        script.onload = () => {
          this.log('heic-to library loaded dynamically');
          resolve();
        };
        script.onerror = () => reject(new Error('Failed to load HEIC conversion library'));
        document.head.appendChild(script);
      });
    }
    return this._heicLoadPromise;
  }

  /**
   * Try to decode HEIC using the browser's native image decoding.
   * Safari supports HEIC natively. If the browser can display it,
   * we draw to canvas and export as JPEG — no library needed.
   * Returns null if the browser can't decode the file.
   */
  tryNativeHeicDecode(file) {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const img = new Image();

      const timeout = setTimeout(() => {
        cleanup();
        this.log('Native HEIC decode timed out');
        resolve(null);
      }, 5000);

      const cleanup = () => {
        clearTimeout(timeout);
        URL.revokeObjectURL(url);
        img.onload = null;
        img.onerror = null;
      };

      img.onload = () => {
        cleanup();
        this.log('Browser decoded HEIC natively', { width: img.naturalWidth, height: img.naturalHeight });

        let drawWidth = img.naturalWidth;
        let drawHeight = img.naturalHeight;
        const mp = (drawWidth * drawHeight) / 1_000_000;
        if (mp > this.MAX_MEGAPIXELS) {
          const scale = Math.sqrt(this.MAX_MEGAPIXELS / mp);
          drawWidth = Math.round(drawWidth * scale);
          drawHeight = Math.round(drawHeight * scale);
          this.log(`Native decode: downscaling from ${mp.toFixed(1)} MP to fit canvas limits`);
        }

        const canvas = document.createElement('canvas');
        canvas.width = drawWidth;
        canvas.height = drawHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, drawWidth, drawHeight);

        if (this.isCanvasBlank(canvas)) {
          this.log('Native decode produced blank canvas');
          resolve(null);
          return;
        }

        canvas.toBlob(
          (blob) => {
            if (!blob) { resolve(null); return; }
            const newFile = new File(
              [blob],
              file.name.replace(/\.(heic|heif)$/i, '.jpg'),
              { type: 'image/jpeg', lastModified: Date.now() }
            );
            resolve(newFile);
          },
          'image/jpeg',
          0.95
        );
      };

      img.onerror = () => {
        cleanup();
        this.log('Browser cannot decode HEIC natively');
        resolve(null);
      };

      img.src = url;
    });
  }

  /**
   * Downscale images exceeding iOS Safari's ~16MP canvas limit.
   * Returns the original file if within limits.
   * @param {File} file
   * @param {{ preserveFormat?: boolean }} options
   */
  async downscaleIfNeeded(file, { preserveFormat = false } = {}) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        const megapixels = (img.naturalWidth * img.naturalHeight) / 1_000_000;

        if (megapixels <= this.MAX_MEGAPIXELS) {
          this.log(`Image ${img.naturalWidth}×${img.naturalHeight} (${megapixels.toFixed(1)} MP) — within limits`);
          resolve(file);
          return;
        }

        this.log(`Image ${img.naturalWidth}×${img.naturalHeight} (${megapixels.toFixed(1)} MP) — downscaling`);
        this.setLoadingText('Optimizing large image...');

        const scale = Math.sqrt(this.MAX_MEGAPIXELS / megapixels);
        const newWidth = Math.round(img.naturalWidth * scale);
        const newHeight = Math.round(img.naturalHeight * scale);

        const canvas = document.createElement('canvas');
        canvas.width = newWidth;
        canvas.height = newHeight;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        if (this.isCanvasBlank(canvas)) {
          this.log('Downscaled canvas is blank — trying lower resolution');
          const retryScale = scale * 0.7;
          canvas.width = Math.round(img.naturalWidth * retryScale);
          canvas.height = Math.round(img.naturalHeight * retryScale);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          if (this.isCanvasBlank(canvas)) {
            reject(new Error('Unable to process this image. Please try a smaller file or a different format.'));
            return;
          }
        }

        const outputType = preserveFormat && ['image/png', 'image/webp'].includes(file.type)
          ? file.type
          : 'image/jpeg';

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to process image. Please try again.'));
              return;
            }

            const downscaledFile = new File([blob], file.name, { type: outputType, lastModified: Date.now() });
            this.log(`Downscaled to ${newWidth}×${newHeight}`, {
              originalMB: (file.size / 1024 / 1024).toFixed(1),
              newMB: (downscaledFile.size / 1024 / 1024).toFixed(1),
              outputType,
            });
            resolve(downscaledFile);
          },
          outputType,
          outputType === 'image/png' ? undefined : 0.95
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image for processing. The file may be corrupted.'));
      };

      img.src = url;
    });
  }

  /**
   * Sample multiple pixel regions to detect blank/white canvas output.
   * iOS Safari silently produces blank canvases when memory is exhausted.
   */
  isCanvasBlank(canvas) {
    const ctx = canvas.getContext('2d');
    if (!ctx) return true;

    const w = canvas.width;
    const h = canvas.height;

    const regions = [
      { x: Math.floor(w * 0.25), y: Math.floor(h * 0.25) },
      { x: Math.floor(w * 0.5), y: Math.floor(h * 0.5) },
      { x: Math.floor(w * 0.75), y: Math.floor(h * 0.75) },
      { x: Math.floor(w * 0.1), y: Math.floor(h * 0.9) },
      { x: Math.floor(w * 0.9), y: Math.floor(h * 0.1) },
    ];

    let nonBlankCount = 0;
    for (const { x, y } of regions) {
      const pixel = ctx.getImageData(x, y, 1, 1).data;
      const isBlank = pixel[3] === 0 || (pixel[0] === 0 && pixel[1] === 0 && pixel[2] === 0 && pixel[3] === 0);
      if (!isBlank) nonBlankCount++;
    }

    const blank = nonBlankCount === 0;
    this.log('Blank detection:', { blank, nonBlankCount, totalSampled: regions.length });
    return blank;
  }

  /**
   * Load an image URL into an <img> element with a timeout.
   * @returns {Promise<void>}
   */
  loadImageWithTimeout(url, imgEl, timeoutMs = 10000) {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        imgEl.onload = null;
        imgEl.onerror = null;
        reject(new Error('Image load timed out. Please try a different image.'));
      }, timeoutMs);

      if (imgEl.src === url && imgEl.complete) {
        clearTimeout(timeout);
        resolve();
        return;
      }

      imgEl.onload = () => {
        clearTimeout(timeout);
        imgEl.onload = null;
        imgEl.onerror = null;
        this.log('Image loaded into crop element', { width: imgEl.naturalWidth, height: imgEl.naturalHeight });
        resolve();
      };

      imgEl.onerror = (e) => {
        clearTimeout(timeout);
        imgEl.onload = null;
        imgEl.onerror = null;
        reject(new Error('Failed to load image. Please try a different file.'));
      };

      imgEl.src = url;
    });
  }

  // --- Loading overlay ---

  showLoading(text = '') {
    if (!this.loadingOverlay) return;
    this.loadingOverlay.classList.add('active');
    this.setLoadingText(text);

    this.loadingTimeout = setTimeout(() => {
      this.setLoadingText(this.strings.stillWorking || 'Still working...');
    }, 5000);
  }

  hideLoading() {
    if (!this.loadingOverlay) return;
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }
    this.setLoadingText('');
    this.loadingOverlay.classList.remove('active');
  }

  setLoadingText(text) {
    if (this.loadingText) this.loadingText.textContent = text;
  }

  // --- Error display ---

  showError(message) {
    alert(message);
  }

  // --- S3 Upload & Cart Integration (Phase 5) ---

  /**
   * Get a presigned S3 URL from the Lambda, then PUT the file to S3.
   * @param {Blob|File} file
   * @param {string} bucket - one of: v1-puz, v1-box, v1-ref, v1-preview
   * @param {string} contentType
   * @returns {Promise<string>} public S3 URL of the uploaded file
   */
  async uploadToS3(file, bucket, contentType = 'image/jpeg') {
    const apiUrl = this.config.uploadApiUrl;
    if (!apiUrl || apiUrl.includes('PLACEHOLDER')) {
      throw new Error('Upload API URL not configured');
    }

    const presignRes = await fetch(`${apiUrl}?bucket=${encodeURIComponent(bucket)}&contentType=${encodeURIComponent(contentType)}`);
    if (!presignRes.ok) {
      const errBody = await presignRes.text();
      throw new Error(`Failed to get upload URL: ${presignRes.status} ${errBody}`);
    }

    const { uploadURL, publicURL } = await presignRes.json();

    const uploadRes = await fetch(uploadURL, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: file,
    });

    if (!uploadRes.ok) {
      throw new Error(`S3 upload failed: ${uploadRes.status}`);
    }

    this.log(`Uploaded to ${bucket}`, { publicURL, size: file.size });
    return publicURL;
  }

  /** Convert a canvas to a JPEG Blob. */
  canvasToBlob(canvas, type = 'image/jpeg', quality = 0.92) {
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => blob ? resolve(blob) : reject(new Error('Canvas to blob failed')),
        type,
        quality
      );
    });
  }

  /**
   * Full add-to-cart flow:
   * 1. Validate state
   * 2. Upload all files to S3
   * 3. Build cart payload
   * 4. POST to Shopify cart API
   * 5. Update cart count
   * 6. Show success
   */
  async handleAddToCart() {
    const variant = this.resolveVariant();
    if (!variant?.id) {
      this.showError('Could not determine the correct product variant. Please try again.');
      return;
    }

    if (!this.rightsCheckbox?.checked) {
      this.showError('Please confirm image rights before adding to cart.');
      return;
    }

    if (this.hasCustomNote && !this.noteTextArea?.value.trim()) {
      this.showError('Please enter your custom note text, or uncheck the custom note option.');
      return;
    }

    if (!this.validateCustomizationUploads()) return;

    if (this.addToCartBtn) this.addToCartBtn.disabled = true;
    this.stageManager.goTo('processing');
    const processingText = document.getElementById('pzy-processing-text');

    try {
      const uploads = {};

      if (processingText) processingText.textContent = 'Uploading your puzzle image...';
      const puzzleBlob = await this.canvasToBlob(this.croppedImageCanvas, 'image/jpeg', 0.95);
      uploads.puzzleUrl = await this.uploadToS3(puzzleBlob, 'v1-puz');

      if (this.hasCustomImage && this.customImageBlob) {
        if (processingText) processingText.textContent = 'Uploading custom image...';
        uploads.customImageUrl = await this.uploadToS3(this.customImageBlob, 'v1-box');
      }

      if (this.hasCustomLogo && this.customLogoBlob) {
        if (processingText) processingText.textContent = 'Uploading custom logo...';
        const logoType = this.customLogoBlob.type || 'image/png';
        uploads.logoUrl = await this.uploadToS3(this.customLogoBlob, 'v1-box', logoType);
      }

      if (this.finalPreviewCanvas) {
        if (processingText) processingText.textContent = 'Uploading box preview...';
        const boxPreviewBlob = await this.canvasToBlob(this.finalPreviewCanvas, 'image/png');
        uploads.boxPreviewUrl = await this.uploadToS3(boxPreviewBlob, 'v1-preview', 'image/png');
      }

      if (!this.puzzlePreviewCanvas) await this.generatePuzzlePreview();

      if (processingText) processingText.textContent = 'Adding to cart...';

      const currentRatio = this.getAspectRatio();
      const addons = [];
      if (this.hasCustomNote) addons.push('note');
      if (this.hasCustomImage) addons.push('image');
      if (this.hasCustomLogo) addons.push('logo');

      const size = this.selectedPieceCount;
      const variantConfig = addons.length > 0 ? `${size}pc-${addons.join('-')}` : `${size}pc`;

      const storewideModifier = this.config.storewideUpsellModifier;
      const customerModifier = this.config.customerUpsellModifier;
      const activeModifier = customerModifier || storewideModifier;
      let cartVariantId = variant.id;

      if (activeModifier) {
        const baseVariant = this.config.variants?.standard?.[this.selectedPieceCount]?.none;
        if (baseVariant) {
          cartVariantId = baseVariant.id;
        }
      }

      const formData = new FormData();
      formData.append('id', cartVariantId);
      formData.append('quantity', this.quantity);

      if (this.finalPreviewCanvas) {
        const boxFile = new File([await this.canvasToBlob(this.finalPreviewCanvas, 'image/png')], `box-preview-${Date.now()}.png`, { type: 'image/png' });
        formData.append('properties[Puzzle box preview]', boxFile);
      }
      if (this.puzzlePreviewCanvas) {
        const puzzleFile = new File([await this.canvasToBlob(this.puzzlePreviewCanvas, 'image/png')], `puzzle-preview-${Date.now()}.png`, { type: 'image/png' });
        formData.append('properties[Puzzle preview]', puzzleFile);
      }

      formData.append('properties[Heads up]', "the default photo is shown here, but we'll use your custom image 💚");

      const bonusMessage = this._getBonusMessage(activeModifier, addons);
      if (bonusMessage) formData.append('properties[Bonus]', bonusMessage);

      formData.append('properties[_Puzzle box preview url]', uploads.boxPreviewUrl || '');
      formData.append('properties[_Print File]', uploads.puzzleUrl || '');
      formData.append('properties[_Variant config]', variantConfig);
      formData.append('properties[_selected_variant]', String(variant.id));
      formData.append('properties[_aspect_ratio]', String(currentRatio));
      formData.append('properties[_note_text]', this.hasCustomNote ? (this.noteTextArea?.value.trim() || '') : '');

      if (this.hasCustomImage && uploads.customImageUrl) {
        formData.append('properties[_Box cover image]', uploads.customImageUrl);
        formData.append('properties[_custom_ref_image]', this.useCustomForReference ? 'true' : 'false');
      }
      if (this.hasCustomLogo && uploads.logoUrl) formData.append('properties[_custom_logo_url]', uploads.logoUrl);
      if (activeModifier) formData.append('properties[_upsell_modifier]', activeModifier);

      this.log('Cart payload', { variantId: cartVariantId, quantity: this.quantity, variantConfig });

      const cartRes = await fetch('/cart/add.js', { method: 'POST', body: formData });

      if (!cartRes.ok) {
        const errText = await cartRes.text();
        throw new Error(`Add to cart failed: ${cartRes.status} ${errText}`);
      }

      await this.updateCartCount();

      this.stageManager.goTo('success');
      this.log('Added to cart successfully');

    } catch (error) {
      console.error('[PZY] Add to cart error:', error);
      this.stageManager.goTo('preview');
      if (this.addToCartBtn) this.addToCartBtn.disabled = !this.rightsCheckbox?.checked;
      this.showError(`Something went wrong: ${error.message}. Please try again.`);
    }
  }

  /**
   * Bulk add-to-cart flow.
   * 1. Validate bulk config
   * 2. Determine order type
   * 3. Resolve variant (multi-recipient conversion if needed)
   * 4. Upload files (same pipeline as standard)
   * 5. Generate enhanced CSV
   * 6. POST to cart with bulk line item properties
   */
  async handleBulkAddToCart() {
    if (!this.bulkManager) return;

    const shipping = this.bulkManager.getSelectedShipping();
    if (!shipping) {
      this.showError(this.strings.errorSelectShipping || 'Please select a shipping option.');
      return;
    }

    if (!this.rightsCheckbox?.checked) {
      this.showError('Please confirm image rights before adding to cart.');
      return;
    }

    const noteText = this.hasCustomNote ? (this.noteTextArea?.value || '') : '';
    const orderType = this.bulkManager.getOrderType(noteText, this.hasCustomNote);
    if (!orderType) return;

    const recipients = this.bulkManager.getRecipients(orderType);
    if (recipients.length === 0) {
      this.showError(this.strings.errorNoRecipients || 'No recipients found.');
      return;
    }

    const actualQuantity = this.bulkManager.getTotalQuantity(orderType);
    const isMultiRecipient = orderType.startsWith('multi_recipient');

    let variant = this.resolveVariant();
    if (!variant?.id) {
      this.showError('Could not determine the correct product variant. Please try again.');
      return;
    }

    let cartVariantId = variant.id;

    if (isMultiRecipient) {
      const comboKey = this.getVariantComboKey();
      const multiVariant = this.config.variants?.multiRecipient?.[this.selectedPieceCount]?.[comboKey];
      if (multiVariant?.id) {
        cartVariantId = multiVariant.id;
        this.log('Multi-recipient variant conversion:', variant.id, '->', cartVariantId);
      } else {
        this.log('No multi-recipient variant found, using bulk variant');
      }
    }

    if (this.bulkAddToCartBtn) this.bulkAddToCartBtn.disabled = true;
    this.stageManager.goTo('processing');
    const processingText = document.getElementById('pzy-processing-text');

    try {
      const uploads = {};

      if (processingText) processingText.textContent = 'Uploading your puzzle image...';
      const puzzleBlob = await this.canvasToBlob(this.croppedImageCanvas, 'image/jpeg', 0.95);
      uploads.puzzleUrl = await this.uploadToS3(puzzleBlob, 'v1-puz');

      if (this.hasCustomImage && this.customImageBlob) {
        if (processingText) processingText.textContent = 'Uploading custom image...';
        uploads.customImageUrl = await this.uploadToS3(this.customImageBlob, 'v1-box');
      }

      if (this.hasCustomLogo && this.customLogoBlob) {
        if (processingText) processingText.textContent = 'Uploading custom logo...';
        const logoType = this.customLogoBlob.type || 'image/png';
        uploads.logoUrl = await this.uploadToS3(this.customLogoBlob, 'v1-box', logoType);
      }

      if (this.finalPreviewCanvas) {
        if (processingText) processingText.textContent = 'Uploading box preview...';
        const boxPreviewBlob = await this.canvasToBlob(this.finalPreviewCanvas, 'image/png');
        uploads.boxPreviewUrl = await this.uploadToS3(boxPreviewBlob, 'v1-preview', 'image/png');
      }

      if (!this.puzzlePreviewCanvas) await this.generatePuzzlePreview();

      if (processingText) processingText.textContent = this.strings.addingToCart || 'Adding to cart...';

      const currentRatio = this.getAspectRatio();
      const isLandscape = currentRatio >= 1;
      const addons = [];
      if (this.hasCustomNote) addons.push('note');
      if (this.hasCustomImage) addons.push('image');
      if (this.hasCustomLogo) addons.push('logo');

      const size = this.selectedPieceCount;
      const variantConfig = addons.length > 0 ? `${size}pc-${addons.join('-')}` : `${size}pc`;

      const suppressCheckbox = document.getElementById('pzy-suppress-emails');
      const suppressEmails = isMultiRecipient && !suppressCheckbox?.checked;

      const enhancedCsv = this.bulkManager.generateEnhancedCsv(recipients, orderType, {
        puzzlePrint: uploads.puzzleUrl || '',
        boxCover: uploads.customImageUrl || '',
        preview: uploads.boxPreviewUrl || ''
      }, {
        size: `${size}`,
        orientation: isLandscape ? 'Landscape' : 'Portrait',
        variantId: String(cartVariantId),
        customNote: noteText,
        hasCustomImage: this.hasCustomImage,
        hasCustomLogo: this.hasCustomLogo,
        suppressEmails
      });

      const storewideModifier = this.config.storewideUpsellModifier;
      const customerModifier = this.config.customerUpsellModifier;
      const activeModifier = customerModifier || storewideModifier;

      if (activeModifier) {
        const productType = isMultiRecipient ? 'multiRecipient' : 'bulk';
        const baseVariant = this.config.variants?.[productType]?.[this.selectedPieceCount]?.none;
        if (baseVariant) cartVariantId = baseVariant.id;
      }

      const formData = new FormData();
      formData.append('id', cartVariantId);
      formData.append('quantity', actualQuantity);

      if (this.finalPreviewCanvas) {
        const boxFile = new File([await this.canvasToBlob(this.finalPreviewCanvas, 'image/png')], `box-preview-${Date.now()}.png`, { type: 'image/png' });
        formData.append('properties[Puzzle box preview]', boxFile);
      }
      if (this.puzzlePreviewCanvas) {
        const puzzleFile = new File([await this.canvasToBlob(this.puzzlePreviewCanvas, 'image/png')], `puzzle-preview-${Date.now()}.png`, { type: 'image/png' });
        formData.append('properties[Puzzle preview]', puzzleFile);
      }

      formData.append('properties[Heads up]', "the default photo is shown here, but we'll use your custom image 💚");
      formData.append('properties[_order_type]', orderType.replace(/_/g, ' '));
      formData.append('properties[Note]', this.strings.bulkOrderReviewNote || 'Your bulk order will automatically be submitted once reviewed');

      const bonusMessage = this._getBonusMessage(activeModifier, addons);
      if (bonusMessage) formData.append('properties[Bonus]', bonusMessage);

      formData.append('properties[_Puzzle box preview url]', uploads.boxPreviewUrl || '');
      formData.append('properties[_Print File]', uploads.puzzleUrl || '');
      formData.append('properties[_Variant config]', variantConfig);
      formData.append('properties[_selected_variant]', String(variant.id));
      formData.append('properties[_aspect_ratio]', String(currentRatio));
      formData.append('properties[_note_text]', noteText || '');
      if (this.hasCustomImage && uploads.customImageUrl) formData.append('properties[_Box cover image]', uploads.customImageUrl);
      if (this.hasCustomLogo && uploads.logoUrl) formData.append('properties[_custom_logo_url]', uploads.logoUrl);
      if (suppressEmails) formData.append('properties[_suppress_emails]', 'true');
      if (activeModifier) formData.append('properties[_upsell_modifier]', activeModifier);

      formData.append('properties[_enhanced_csv]', enhancedCsv);

      this.log('Bulk cart payload', { variantId: cartVariantId, quantity: actualQuantity, orderType, variantConfig });

      const cartRes = await fetch('/cart/add.js', { method: 'POST', body: formData });
      if (!cartRes.ok) {
        const errText = await cartRes.text();
        throw new Error(`Add to cart failed: ${cartRes.status} ${errText}`);
      }

      await this.updateCartCount();
      this.stageManager.goTo('success');
      this.log('Bulk order added to cart successfully');

    } catch (error) {
      console.error('[PZY] Bulk add to cart error:', error);
      this.stageManager.goTo('bulk-config');
      if (this.bulkAddToCartBtn) this.bulkAddToCartBtn.disabled = false;
      this.showError(`Something went wrong: ${error.message}. Please try again.`);
    }
  }

  /** Notify the theme that the cart changed. The theme's cart-icon component handles the bubble. */
  async updateCartCount() {
    try {
      const res = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();

      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { data: { sections: {}, itemCount: cart.item_count }, sourceId: 'pzy-customizer' }
      }));

      this.log('Cart updated:', cart.item_count);
    } catch (err) {
      this.log('Cart update failed (non-critical):', err.message);
    }
  }

  // --- Box Preview Generation ---

  /**
   * Return a canvas with bleed edges trimmed based on the selected piece count's
   * print vs cut dimensions. Used for box cover and puzzle preview (not the print file).
   */
  getTrimmedCanvas(sourceCanvas) {
    const dims = this.config.printDimensions?.[this.selectedPieceCount];
    if (!dims) return sourceCanvas;

    const isLandscape = this.getAspectRatio() >= 1;
    const printW = isLandscape ? dims.printHeight : dims.printWidth;
    const printH = isLandscape ? dims.printWidth : dims.printHeight;
    const cutW = isLandscape ? dims.cutHeight : dims.cutWidth;
    const cutH = isLandscape ? dims.cutWidth : dims.cutHeight;

    const bleedX = (printW - cutW) / 2 / printW;
    const bleedY = (printH - cutH) / 2 / printH;
    if (bleedX <= 0 && bleedY <= 0) return sourceCanvas;

    const sw = sourceCanvas.width;
    const sh = sourceCanvas.height;
    const tx = Math.round(sw * bleedX);
    const ty = Math.round(sh * bleedY);
    const tw = sw - tx * 2;
    const th = sh - ty * 2;

    const trimmed = document.createElement('canvas');
    trimmed.width = tw;
    trimmed.height = th;
    trimmed.getContext('2d').drawImage(sourceCanvas, tx, ty, tw, th, 0, 0, tw, th);
    return trimmed;
  }

  /** Get the bleed trim percentages for the current piece count and orientation. */
  getBleedPercents() {
    const dims = this.config.printDimensions?.[this.selectedPieceCount];
    if (!dims) return { x: 0, y: 0 };
    const isLandscape = this.getAspectRatio() >= 1;
    const printW = isLandscape ? dims.printHeight : dims.printWidth;
    const printH = isLandscape ? dims.printWidth : dims.printHeight;
    const cutW = isLandscape ? dims.cutHeight : dims.cutWidth;
    const cutH = isLandscape ? dims.cutWidth : dims.cutHeight;
    return {
      x: (printW - cutW) / 2 / printW * 100,
      y: (printH - cutH) / 2 / printH * 100
    };
  }

  /**
   * Load an image from a URL or Blob and return a promise that resolves to an Image element.
   * Used for loading box template PNGs from CDN and custom uploads.
   */
  loadImage(source) {
    if (typeof source === 'string' && this.imageCache.has(source)) {
      return Promise.resolve(this.imageCache.get(source));
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      if (source instanceof Blob || source instanceof File) {
        const url = URL.createObjectURL(source);
        img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load image')); };
        img.src = url;
      } else {
        img.onload = () => { this.imageCache.set(source, img); resolve(img); };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = source;
      }
    });
  }

  /** Draw an image with rounded corners by clipping to a rounded rect path. */
  drawRoundedImage(ctx, img, x, y, width, height, radius) {
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(img, x, y, width, height);
    ctx.restore();
  }

  /**
   * Generate the box preview composite:
   * box template + cropped image (or custom image) + custom logo + note text + drop shadow.
   */
  async generateBoxPreview(advanceStage = false) {
    if (!this.croppedImageCanvas) return null;
    const maxSize = 900;
    const spinner = document.getElementById('pzy-preview-spinner');
    if (spinner && !advanceStage) spinner.classList.add('active');

    try {
      const currentRatio = this.getAspectRatio();
      const orientation = currentRatio >= 1 ? 'horizontal' : 'vertical';
      const size = this.selectedPieceCount;

      this.log('Generating box preview', { size, orientation, aspectRatio: currentRatio, advanceStage });

      const previewCanvas = document.createElement('canvas');
      const ctx = previewCanvas.getContext('2d');
      previewCanvas.width = maxSize;
      previewCanvas.height = maxSize;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      const templateUrl = this.config.boxTemplates?.[orientation]?.[size];
      if (templateUrl) {
        const boxImage = await this.loadImage(templateUrl);
        ctx.drawImage(boxImage, 0, 0, maxSize, maxSize);
      } else {
        ctx.fillStyle = '#f0f0f0';
        ctx.fillRect(0, 0, maxSize, maxSize);
        this.log('No box template URL found, using grey background');
      }

      const layout = this.config.boxLayout?.[size]?.[orientation] || { y: 0.15, width: 0.7, height: 0.7 };
      const areaW = maxSize * layout.width;
      const areaH = maxSize * layout.height;
      const area = { x: (maxSize - areaW) / 2, y: maxSize * layout.y, width: areaW, height: areaH };

      const rawSource = (this.hasCustomImage && this.customImageBlob)
        ? await this.loadImage(this.customImageBlob)
        : this.croppedImageCanvas;
      const sourceCanvas = (rawSource === this.croppedImageCanvas)
        ? this.getTrimmedCanvas(rawSource)
        : rawSource;

      const srcRatio = (sourceCanvas.naturalWidth || sourceCanvas.width) / (sourceCanvas.naturalHeight || sourceCanvas.height);
      const areaRatio = area.width / area.height;
      let imgW, imgH;
      if (srcRatio > areaRatio) {
        imgW = area.width;
        imgH = imgW / srcRatio;
      } else {
        imgH = area.height;
        imgW = imgH * srcRatio;
      }
      const imgX = area.x + (area.width - imgW) / 2;
      const imgY = area.y + (area.height - imgH) / 2;

      ctx.save();
      const radius = 6;
      ctx.beginPath();
      ctx.moveTo(imgX + radius, imgY);
      ctx.lineTo(imgX + imgW - radius, imgY);
      ctx.quadraticCurveTo(imgX + imgW, imgY, imgX + imgW, imgY + radius);
      ctx.lineTo(imgX + imgW, imgY + imgH - radius);
      ctx.quadraticCurveTo(imgX + imgW, imgY + imgH, imgX + imgW - radius, imgY + imgH);
      ctx.lineTo(imgX + radius, imgY + imgH);
      ctx.quadraticCurveTo(imgX, imgY + imgH, imgX, imgY + imgH - radius);
      ctx.lineTo(imgX, imgY + radius);
      ctx.quadraticCurveTo(imgX, imgY, imgX + radius, imgY);
      ctx.closePath();
      ctx.strokeStyle = 'white';
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.clip();
      ctx.drawImage(sourceCanvas, imgX, imgY, imgW, imgH);
      ctx.restore();

      await this.drawLogoOnBox(ctx, maxSize, orientation);
      this.drawNoteOnBox(ctx, maxSize, orientation);

      const shadowCanvas = document.createElement('canvas');
      shadowCanvas.width = maxSize;
      shadowCanvas.height = maxSize;
      const shadowCtx = shadowCanvas.getContext('2d');
      shadowCtx.shadowColor = 'rgba(0, 0, 0, 0.4)';
      shadowCtx.shadowBlur = 25;
      shadowCtx.shadowOffsetX = 15;
      shadowCtx.shadowOffsetY = 10;
      shadowCtx.drawImage(previewCanvas, 0, 0);

      this.finalPreviewCanvas = shadowCanvas;

      if (this.previewImage) {
        const displayCanvas = document.createElement('canvas');
        displayCanvas.width = maxSize;
        displayCanvas.height = maxSize;
        const displayCtx = displayCanvas.getContext('2d');
        displayCtx.fillStyle = '#FFFFFF';
        displayCtx.fillRect(0, 0, maxSize, maxSize);
        displayCtx.drawImage(shadowCanvas, 0, 0);
        this.previewImage.src = displayCanvas.toDataURL('image/jpeg', 0.92);
      }

      if (spinner) spinner.classList.remove('active');
      this.log('Box preview generated');

      if (advanceStage) {
        this.stageManager.goTo('preview');
      }

      return previewCanvas;
    } catch (error) {
      if (spinner) spinner.classList.remove('active');
      console.error('[PZY] Box preview error:', error);
      this.showError('Error generating preview. Please try again.');
      return null;
    }
  }

  /** Draw logo onto the box preview — custom upload or default Puzzery logo. */
  async drawLogoOnBox(ctx, maxSize, orientation) {
    try {
      const isCustom = this.hasCustomLogo && this.customLogoBlob;
      const logoSource = isCustom ? this.customLogoBlob : this.config.boxLogoConfig?.defaultUrl;
      if (!logoSource) return;

      const logoImg = await this.loadImage(logoSource);
      const logoConf = this.config.boxLogoConfig?.[this.selectedPieceCount]?.[orientation] || { scale: 0.30, y: 0.12 };
      const containerRatio = this.config.boxLogoConfig?.containerAspectRatio || 4;

      const containerW = maxSize * logoConf.scale;
      const containerH = containerW / containerRatio;
      const containerX = (maxSize - containerW) / 2;
      const containerY = maxSize * logoConf.y - containerH / 2;

      const logoRatio = logoImg.naturalWidth / logoImg.naturalHeight;
      let drawW, drawH;
      if (logoRatio > containerRatio) {
        drawW = containerW;
        drawH = drawW / logoRatio;
      } else {
        drawH = containerH;
        drawW = drawH * logoRatio;
      }
      const drawX = containerX + (containerW - drawW) / 2;
      const drawY = containerY + (containerH - drawH) / 2;
      ctx.drawImage(logoImg, drawX, drawY, drawW, drawH);
    } catch (err) {
      this.log('Failed to draw logo on box:', err.message);
    }
  }

  /** Draw the note text (or default text) onto the box preview canvas. */
  drawNoteOnBox(ctx, maxSize, orientation) {
    const textConf = this.config.boxTextConfig || {};
    const sizeConf = textConf[this.selectedPieceCount]?.[orientation] || {};

    if (this.hasCustomNote) {
      const noteVal = this.noteTextArea?.value.trim() || '';
      if (!noteVal) return;
    }

    let displayText;
    if (this.hasCustomNote && this.noteTextArea?.value.trim()) {
      displayText = this.noteTextArea.value.trim();
      displayText = displayText.replace(/\[first_name\]/gi, 'Albert').replace(/\[last_name\]/gi, 'Einstein');
    } else {
      displayText = textConf.defaultText || 'Crafted with care for you and the planet';
    }

    const yPos = sizeConf.yPosition || 0.85;
    const fontSize = Math.round(maxSize * (textConf.fontSize || 0.0125));
    const lineHeight = maxSize * (textConf.lineHeight || 0.0135);
    const maxLineWidth = maxSize * (sizeConf.maxWidth || 0.5);
    const maxLines = textConf.maxLines || 2;

    ctx.fillStyle = textConf.color || '#255553';
    ctx.textAlign = 'center';
    ctx.font = `600 ${fontSize}px Poppins, sans-serif`;

    const textX = maxSize / 2 - 6;
    const textY = maxSize * yPos;

    const inputLines = displayText.split('\n');
    const finalLines = [];

    for (let i = 0; i < inputLines.length && finalLines.length < maxLines; i++) {
      const line = inputLines[i].trim();
      if (!line) continue;

      if (ctx.measureText(line).width <= maxLineWidth) {
        finalLines.push(line);
      } else {
        const words = line.split(' ');
        let currentLine = '';
        for (let j = 0; j < words.length; j++) {
          const testLine = currentLine + (currentLine ? ' ' : '') + words[j];
          if (ctx.measureText(testLine).width > maxLineWidth && j > 0) {
            finalLines.push(currentLine);
            if (finalLines.length >= maxLines) break;
            currentLine = words[j];
          } else {
            currentLine = testLine;
          }
        }
        if (finalLines.length < maxLines && currentLine) {
          finalLines.push(currentLine);
        }
      }
    }

    const totalTextHeight = (finalLines.length - 1) * lineHeight;
    const startY = textY - totalTextHeight / 2;

    finalLines.slice(0, maxLines).forEach((line, index) => {
      ctx.fillText(line, textX, startY + index * lineHeight);
    });
  }

  /**
   * Generate the puzzle preview: customer's image with puzzle texture overlaid via multiply blend.
   * The puzzle template is horizontal — rotated 90° for portrait orientation.
   */
  async generatePuzzlePreview() {
    if (!this.croppedImageCanvas) return null;
    const maxSize = 900;
    const spinner = document.getElementById('pzy-preview-spinner');
    if (spinner) spinner.classList.add('active');

    try {
      const currentRatio = this.getAspectRatio();
      const isLandscape = currentRatio >= 1;
      const size = this.selectedPieceCount;

      this.log('Generating puzzle preview', { size, isLandscape });

      const canvas = document.createElement('canvas');
      canvas.width = maxSize;
      canvas.height = maxSize;
      const ctx = canvas.getContext('2d');

      const sourceCanvas = this.getTrimmedCanvas(this.croppedImageCanvas);
      const srcW = sourceCanvas.width;
      const srcH = sourceCanvas.height;
      const srcRatio = srcW / srcH;

      let drawW, drawH;
      if (srcRatio >= 1) {
        drawW = maxSize * 0.88;
        drawH = drawW / srcRatio;
      } else {
        drawH = maxSize * 0.88;
        drawW = drawH * srcRatio;
      }
      const drawX = (maxSize - drawW) / 2;
      const drawY = (maxSize - drawH) / 2;

      const borderRadius = 8;
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(drawX + borderRadius, drawY);
      ctx.lineTo(drawX + drawW - borderRadius, drawY);
      ctx.quadraticCurveTo(drawX + drawW, drawY, drawX + drawW, drawY + borderRadius);
      ctx.lineTo(drawX + drawW, drawY + drawH - borderRadius);
      ctx.quadraticCurveTo(drawX + drawW, drawY + drawH, drawX + drawW - borderRadius, drawY + drawH);
      ctx.lineTo(drawX + borderRadius, drawY + drawH);
      ctx.quadraticCurveTo(drawX, drawY + drawH, drawX, drawY + drawH - borderRadius);
      ctx.lineTo(drawX, drawY + borderRadius);
      ctx.quadraticCurveTo(drawX, drawY, drawX + borderRadius, drawY);
      ctx.closePath();
      ctx.clip();

      ctx.drawImage(sourceCanvas, drawX, drawY, drawW, drawH);

      const templateUrl = this.config.puzzleTemplates?.[size];
      if (templateUrl) {
        const puzzleImg = await this.loadImage(templateUrl);
        ctx.globalCompositeOperation = 'multiply';

        if (!isLandscape) {
          ctx.save();
          ctx.translate(drawX + drawW / 2, drawY + drawH / 2);
          ctx.rotate(Math.PI / 2);
          const rotW = drawH;
          const rotH = drawW;
          ctx.drawImage(puzzleImg, -rotW / 2, -rotH / 2, rotW, rotH);
          ctx.restore();
        } else {
          ctx.drawImage(puzzleImg, drawX, drawY, drawW, drawH);
        }

        ctx.globalCompositeOperation = 'source-over';
      }

      ctx.restore();

      const shadowCanvas = document.createElement('canvas');
      shadowCanvas.width = maxSize;
      shadowCanvas.height = maxSize;
      const shadowCtx = shadowCanvas.getContext('2d');
      shadowCtx.shadowColor = 'rgba(0, 0, 0, 0.5)';
      shadowCtx.shadowBlur = 2;
      shadowCtx.shadowOffsetX = 1;
      shadowCtx.shadowOffsetY = 1;
      shadowCtx.drawImage(canvas, 0, 0);

      shadowCtx.shadowColor = 'rgba(0, 0, 0, 0.25)';
      shadowCtx.shadowBlur = 10;
      shadowCtx.shadowOffsetX = 4;
      shadowCtx.shadowOffsetY = 5;
      shadowCtx.drawImage(canvas, 0, 0);

      const displayCanvas = document.createElement('canvas');
      displayCanvas.width = maxSize;
      displayCanvas.height = maxSize;
      const displayCtx = displayCanvas.getContext('2d');
      displayCtx.fillStyle = '#FFFFFF';
      displayCtx.fillRect(0, 0, maxSize, maxSize);
      displayCtx.drawImage(shadowCanvas, 0, 0);

      if (this.previewImage) {
        this.previewImage.src = displayCanvas.toDataURL('image/jpeg', 0.92);
      }

      this.puzzlePreviewCanvas = shadowCanvas;

      if (spinner) spinner.classList.remove('active');
      this.log('Puzzle preview generated');
      return shadowCanvas;
    } catch (error) {
      if (spinner) spinner.classList.remove('active');
      console.error('[PZY] Puzzle preview error:', error);
      return null;
    }
  }

  /** Refresh the current preview (box or puzzle, based on active view). */
  async refreshPreview() {
    if (this.stageManager.current() !== 'preview' || !this.croppedImageCanvas) return;
    if (this.currentView === 'puzzle') {
      await this.generatePuzzlePreview();
    } else {
      await this.generateBoxPreview(false);
    }
  }

  // --- Cropper (Phase 3) ---

  /**
   * Get the correct aspect ratio for the selected piece count.
   * Inverts when rotated (portrait ↔ landscape).
   */
  getAspectRatio() {
    const printConfig = this.config.printConfig || {};
    const entry = printConfig[this.selectedPieceCount];
    const baseRatio = entry ? entry.aspectRatio : (19.50 / 15.50);
    return this.isRotated ? (1 / baseRatio) : baseRatio;
  }

  /**
   * Initialize Cropper.js on the crop image element.
   * Crop box is fixed — user moves/zooms the image underneath.
   */
  initCropper() {
    return new Promise((resolve) => {
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }

      const aspectRatio = this.getAspectRatio();
      this.log('Initializing cropper', { aspectRatio, pieceCount: this.selectedPieceCount, isRotated: this.isRotated });

      this.cropper = new Cropper(this.cropImage, {
        aspectRatio,
        viewMode: 1,
        dragMode: 'move',
        autoCropArea: 1,
        restore: false,
        guides: true,
        center: true,
        highlight: false,
        cropBoxMovable: false,
        cropBoxResizable: false,
        toggleDragModeOnDblclick: false,
        background: true,
        modal: true,
        movable: true,
        zoomable: true,
        zoomOnTouch: true,
        zoomOnWheel: true,
        wheelZoomRatio: 0.1,
        zoom: (e) => {
          if (this._initializingZoom) return;
          const newRatio = e.detail.ratio;
          if (this.minZoom && this.maxZoom && (newRatio > this.maxZoom || newRatio < this.minZoom)) {
            e.preventDefault();
            return;
          }
          if (this.zoomSlider && this.minZoom && this.maxZoom) {
            const pct = ((newRatio - this.minZoom) / (this.maxZoom - this.minZoom)) * 100;
            this.zoomSlider.value = Math.round(Math.max(0, Math.min(100, pct)));
          }
        },
        ready: () => {
          setTimeout(() => {
            this._initializingZoom = true;

            const containerData = this.cropper.getContainerData();
            const imageData = this.cropper.getImageData();
            const cropBoxData = this.cropper.getCropBoxData();

            const calcMin = Math.max(
              cropBoxData.width / imageData.naturalWidth,
              cropBoxData.height / imageData.naturalHeight
            );

            this.cropper.zoomTo(0.0001);
            const clampedData = this.cropper.getImageData();
            const probedMin = clampedData.width / clampedData.naturalWidth;

            this.minZoom = Math.max(calcMin, probedMin);
            this.maxZoom = this.minZoom * 3;
            this.initialZoom = this.minZoom;

            this.cropper.zoomTo(this.initialZoom);
            if (this.zoomSlider) this.zoomSlider.value = 0;

            this._initializingZoom = false;

            this.cropper.setCropBoxData({
              left: (containerData.width - cropBoxData.width) / 2,
              top: (containerData.height - cropBoxData.height) / 2,
              width: cropBoxData.width,
              height: cropBoxData.height,
            });

            const bleed = this.getBleedPercents();
            const viewBox = document.querySelector('.cropper-view-box');
            if (viewBox) {
              viewBox.style.setProperty('--bleed-x', `${bleed.x}%`);
              viewBox.style.setProperty('--bleed-y', `${bleed.y}%`);
            }

            this.log('Cropper ready', {
              container: `${containerData.width}×${containerData.height}`,
              image: `${imageData.naturalWidth}×${imageData.naturalHeight}`,
              cropBox: `${cropBoxData.width.toFixed(1)}×${cropBoxData.height.toFixed(1)}`,
              zoomRange: `${this.minZoom.toFixed(4)}–${this.maxZoom.toFixed(4)}`,
              bleed: `${bleed.x.toFixed(2)}% x ${bleed.y.toFixed(2)}%`,
            });
            resolve();
          }, 100);
        },
      });
    });
  }

  /** Map zoom slider (0–100) to the cropper's zoom range. */
  handleZoom(value) {
    if (!this.cropper || !this.initialZoom) return;
    const zoomValue = this.minZoom + (value / 100) * (this.maxZoom - this.minZoom);
    if (isNaN(zoomValue)) return;
    this.cropper.zoomTo(zoomValue);
  }

  /** Toggle portrait <-> landscape. Destroys and reinitializes the cropper for a clean reset. */
  async handleRotation() {
    if (!this.cropper) return;
    this.isRotated = !this.isRotated;
    this.log('Rotating', { isRotated: this.isRotated, newAspectRatio: this.getAspectRatio() });
    await this.initCropper();
  }

  /** Confirm crop: extract cropped canvas, generate box preview, advance. */
  async handleCrop() {
    if (!this.cropper) return;

    try {
      this.croppedImageCanvas = this.cropper.getCroppedCanvas({
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
        fillColor: '#FFFFFF',
      });

      if (!this.croppedImageCanvas) {
        throw new Error('Failed to crop image');
      }

      this.croppedOrientation = this.isRotated;

      if (this.isCanvasBlank(this.croppedImageCanvas)) {
        this.log('Cropped canvas is blank — iOS memory issue likely');
        this.showError('The cropped image came out blank. Please try a smaller photo or a different browser.');
        return;
      }

      this.log('Crop confirmed', {
        orientation: this.croppedOrientation ? 'landscape' : 'portrait',
        canvasSize: `${this.croppedImageCanvas.width}×${this.croppedImageCanvas.height}`,
      });

      this.showLoading('Generating preview...');
      await this.generateBoxPreview(true);
      this.hideLoading();
    } catch (error) {
      this.hideLoading();
      console.error('[PZY] Crop error:', error);
      this.showError('There was a problem processing your image. Please try again.');
    }
  }

  // --- Variant resolution (Phase 4) ---

  /**
   * Build a combo key from selected options, e.g. "note_image" or "none".
   * Keys are alphabetically sorted to match config keys.
   */
  getVariantComboKey() {
    const parts = [];
    if (this.hasCustomNote) parts.push('note');
    if (this.hasCustomImage) parts.push('image');
    if (this.hasCustomLogo) parts.push('logo');
    return parts.length === 0 ? 'none' : parts.join('_');
  }

  /**
   * Resolve the current variant based on piece count + selected options.
   * Returns { id, price } or null if not found.
   */
  resolveVariant() {
    const comboKey = this.getVariantComboKey();
    const productType = this.isBulk ? 'bulk' : 'standard';
    const variants = this.config.variants?.[productType]?.[this.selectedPieceCount];
    const variant = variants?.[comboKey] || null;

    this.log('Variant resolved', {
      pieceCount: this.selectedPieceCount,
      combo: comboKey,
      variantId: variant?.id,
      price: variant?.price,
    });

    return variant;
  }

  // --- View toggle (Box / Puzzle) ---

  handleViewToggle(view) {
    if (view === this.currentView) return;
    this.currentView = view;

    this.viewToggle?.querySelectorAll('.pzy-view-tab').forEach(tab => {
      const isActive = tab.dataset.view === view;
      tab.classList.toggle('pzy-toggle-tab--active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    this.refreshPreview();
    this.log('View toggled:', view);
  }

  // --- Option toggles ---

  handleOptionToggle(option) {
    const checkbox = { note: this.noteCheckbox, image: this.imageCheckbox, logo: this.logoCheckbox }[option];
    const content = { note: this.noteContent, image: this.imageContent, logo: this.logoContent }[option];
    if (!checkbox || !content) return;

    const isChecked = checkbox.checked;
    content.classList.toggle('active', isChecked);

    if (option === 'note') this.hasCustomNote = isChecked;
    if (option === 'image') this.hasCustomImage = isChecked;
    if (option === 'logo') this.hasCustomLogo = isChecked;

    this.resolveVariant();
    this.refreshPreview();
    this.log(`Option toggled: ${option} = ${isChecked}`);
  }

  // --- Custom image/logo uploads ---

  async handleCustomUpload(event, type) {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > this.MAX_UPLOAD_BYTES) {
      this.showError('File size must be less than 20 MB. Please choose a smaller image.');
      event.target.value = '';
      return;
    }

    this.showLoading(`Processing your custom ${type}...`);

    let processedFile = file;
    try {
      const heicResult = await this.convertHeicIfNeeded(file, type);
      processedFile = heicResult.file;

      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];
      if (!allowedTypes.includes(processedFile.type)) {
        throw new Error('Please upload a valid image file (JPEG, PNG, WebP, or SVG).');
      }

      // Match the main puzzle-image safety guard while retaining PNG/WebP
      // transparency. Custom images become JPEGs only after the crop is confirmed.
      if (processedFile.type !== 'image/svg+xml') {
        processedFile = await this.downscaleIfNeeded(processedFile, {
          preserveFormat: true,
        });
      }

      if (type === 'image') {
        this.pendingCustomImageFile = processedFile;
        const customCropImg = document.getElementById('pzy-custom-crop-image');
        if (!customCropImg) throw new Error('Unable to open the image cropper. Please try again.');

        const url = URL.createObjectURL(processedFile);
        await this.loadImageWithTimeout(url, customCropImg, 10000);
        this.stageManager.enableStage('custom-crop');
        this.stageManager.goTo('custom-crop');
        await this.initCustomCropper();
      } else {
        const url = URL.createObjectURL(processedFile);
        const previewEl = document.querySelector('.pzy-custom-logo-preview');
        const previewImg = previewEl?.querySelector('img');
        if (previewImg) previewImg.src = url;
        if (previewEl) previewEl.style.display = '';
        this.customLogoBlob = processedFile;
        const logoInitialBtn = document.getElementById('pzy-logo-upload-initial');
        if (logoInitialBtn) logoInitialBtn.style.display = 'none';
        if (this.currentView === 'puzzle') this.handleViewToggle('box');
        this.refreshPreview();
      }

      this.log(`Custom ${type} uploaded`, { name: processedFile.name, size: processedFile.size });
    } catch (err) {
      this.showError(err.message || 'There was a problem processing this image. Please try again.');
      event.target.value = '';
    } finally {
      this.hideLoading();
    }
  }

  /** Initialize Cropper.js on the custom image — uses the same aspect ratio as the puzzle size. */
  initCustomCropper() {
    return new Promise((resolve) => {
      if (this.customCropper) {
        this.customCropper.destroy();
        this.customCropper = null;
      }

      const customCropImg = document.getElementById('pzy-custom-crop-image');
      if (!customCropImg) { resolve(); return; }

      this.customCropper = new Cropper(customCropImg, {
        aspectRatio: this.getAspectRatio(),
        viewMode: 1,
        dragMode: 'move',
        autoCropArea: 1,
        restore: false,
        guides: true,
        center: true,
        highlight: false,
        cropBoxMovable: false,
        cropBoxResizable: false,
        toggleDragModeOnDblclick: false,
        background: true,
        modal: true,
        movable: true,
        zoomable: true,
        zoomOnTouch: true,
        zoomOnWheel: true,
        wheelZoomRatio: 0.1,
        ready: () => {
          setTimeout(() => {
            const customZoomSlider = document.getElementById('pzy-custom-zoom-slider');
            if (customZoomSlider) customZoomSlider.value = 0;
            this.log('Custom cropper ready');
            resolve();
          }, 100);
        },
      });
    });
  }

  /** Confirm the custom image crop and return to preview. */
  handleCustomCropConfirm() {
    if (!this.customCropper) return;

    const canvas = this.customCropper.getCroppedCanvas({
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
      fillColor: '#FFFFFF',
    });

    if (!canvas) {
      this.showError('Failed to crop the image. Please try again.');
      return;
    }

    canvas.toBlob((blob) => {
      if (!blob) {
        this.showError('Failed to process the cropped image.');
        return;
      }

      this.customImageBlob = new File([blob], 'custom-image.jpg', { type: 'image/jpeg' });

      const url = URL.createObjectURL(blob);
      const previewEl = document.querySelector('.pzy-custom-image-preview');
      const previewImg = previewEl?.querySelector('img');
      if (previewImg) previewImg.src = url;
      if (previewEl) previewEl.style.display = '';

      if (this.customCropper) {
        this.customCropper.destroy();
        this.customCropper = null;
      }

      if (this.refImageCheckbox) this.refImageCheckbox.disabled = false;

      const initialBtn = document.getElementById('pzy-image-upload-initial');
      if (initialBtn) initialBtn.style.display = 'none';

      this.stageManager.goTo('preview');
      if (this.currentView === 'puzzle') this.handleViewToggle('box');
      this.refreshPreview();
      this.log('Custom image crop confirmed');
    }, 'image/jpeg', 0.95);
  }

  handleCustomRemove(type) {
    const previewEl = document.querySelector(`.pzy-custom-${type}-preview`);
    const previewImg = previewEl?.querySelector('img');
    const input = type === 'image' ? this.customImageInput : this.customLogoInput;

    if (previewImg) previewImg.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    if (previewEl) previewEl.style.display = 'none';
    if (input) input.value = '';

    if (type === 'image') {
      this.customImageBlob = null;
      if (this.refImageCheckbox) {
        this.refImageCheckbox.disabled = true;
        this.refImageCheckbox.checked = true;
        this.useCustomForReference = true;
      }
      const imgInitialBtn = document.getElementById('pzy-image-upload-initial');
      if (imgInitialBtn) imgInitialBtn.style.display = '';
    } else {
      this.customLogoBlob = null;
      const logoInitialBtn = document.getElementById('pzy-logo-upload-initial');
      if (logoInitialBtn) logoInitialBtn.style.display = '';
    }

    this.refreshPreview();
    this.log(`Custom ${type} removed`);
  }

  // --- Note input ---

  normalizeNoteLines(value) {
    const maxRows = 2;
    const maxChars = 40;
    const rawLines = value.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');

    if (rawLines.length > 1) {
      return rawLines.slice(0, maxRows).map(line => line.slice(0, maxChars)).join('\n');
    }

    const line = rawLines[0] || '';
    if (line.length <= maxChars) return line;

    const wrapAt = line.lastIndexOf(' ', maxChars);
    if (wrapAt > 0) {
      const firstRow = line.slice(0, wrapAt).trimEnd();
      const secondRow = line.slice(wrapAt + 1).trimStart().slice(0, maxChars);
      return `${firstRow}\n${secondRow}`;
    }

    return `${line.slice(0, maxChars)}\n${line.slice(maxChars, maxChars * maxRows)}`;
  }

  handleNoteInput() {
    if (!this.noteTextArea) return;

    const normalizedValue = this.normalizeNoteLines(this.noteTextArea.value);
    if (this.noteTextArea.value !== normalizedValue) {
      this.noteTextArea.value = normalizedValue;
    }

    const finalLines = this.noteTextArea.value.split('\n');
    if (this.noteRow1Count) this.noteRow1Count.textContent = (finalLines[0] || '').length;
    if (this.noteRow2Count) this.noteRow2Count.textContent = (finalLines[1] || '').length;

    if (this.noteRefreshTimer) clearTimeout(this.noteRefreshTimer);
    this.noteRefreshTimer = setTimeout(() => {
      if (this.currentView === 'puzzle') this.handleViewToggle('box');
      this.refreshPreview();
    }, 300);
  }

  // --- Bonus message (upsell modifier) ---

  _getBonusMessage(activeModifier, addons) {
    if (!activeModifier) return '';
    switch (activeModifier) {
      case 'Free custom notes':
        return addons.includes('note') ? 'Custom note included at no charge' : '';
      case 'Free custom images':
        return addons.includes('image') ? 'Custom image included at no charge' : '';
      case 'Free custom logos':
        return addons.includes('logo') ? 'Custom logo included at no charge' : '';
      case 'Free upsells': {
        const active = addons.filter(a => ['note', 'image', 'logo'].includes(a));
        if (active.length === 0) return '';
        return active.length === 1
          ? `Custom ${active[0]} included at no charge`
          : 'All customizations included at no charge';
      }
      default:
        return '';
    }
  }

  // --- Rights checkbox ---

  handleRightsChange() {
    this.syncRightsGateState();
  }

  syncRightsGateState() {
    if (!this.rightsCheckbox) return;
    if (this.isBulk) {
      const bulkNext = document.getElementById('pzy-bulk-next');
      if (bulkNext) bulkNext.disabled = !this.rightsCheckbox.checked;
    } else {
      if (this.addToCartBtn) this.addToCartBtn.disabled = !this.rightsCheckbox.checked;
    }
  }

  // --- Quantity ---

  handleQuantityChange(delta) {
    const newVal = Math.max(1, Math.min(500, this.quantity + delta));
    this.quantity = newVal;
    if (this.quantityInput) this.quantityInput.value = newVal;
  }

  handleQuantityInput() {
    if (!this.quantityInput) return;
    let val = parseInt(this.quantityInput.value, 10);
    if (isNaN(val) || val < 1) val = 1;
    if (val > 500) val = 500;
    this.quantity = val;
    this.quantityInput.value = val;
  }

  // --- Success actions ---

  handleContinueShopping() {
    this.handleCloseModal();
  }

  // --- Fullscreen preview ---

  handleFullscreenOpen() {
    const previewImg = document.getElementById('pzy-preview-image');
    if (!previewImg || !this.fullscreenImage || !this.fullscreenPreview) return;
    this.fullscreenImage.src = previewImg.src;
    this.fullscreenPreview.classList.add('active');
  }

  handleFullscreenClose() {
    this.fullscreenPreview?.classList.remove('active');
  }

  // --- State reset ---

  /** Reset add-on customizations without resetting the upload/crop state. */
  resetCustomizations() {
    this.hasCustomNote = false;
    this.hasCustomImage = false;
    this.hasCustomLogo = false;
    this.customImageBlob = null;
    this.customLogoBlob = null;
    this.croppedImageCanvas = null;
    this.finalPreviewCanvas = null;
    this.puzzlePreviewCanvas = null;
    this.currentView = 'box';

    if (this.noteTextArea) this.noteTextArea.value = '';
    if (this.noteRow1Count) this.noteRow1Count.textContent = '0';
    if (this.noteRow2Count) this.noteRow2Count.textContent = '0';
    if (this.noteCheckbox) this.noteCheckbox.checked = false;
    if (this.imageCheckbox) this.imageCheckbox.checked = false;
    if (this.logoCheckbox) this.logoCheckbox.checked = false;
    if (this.rightsCheckbox) this.rightsCheckbox.checked = false;
    if (this.addToCartBtn) this.addToCartBtn.disabled = true;
    this.syncRightsGateState();
    if (this.customImageInput) this.customImageInput.value = '';
    if (this.customLogoInput) this.customLogoInput.value = '';
    if (this.refImageCheckbox) {
      this.refImageCheckbox.checked = true;
      this.refImageCheckbox.disabled = true;
    }
    this.useCustomForReference = true;

    [this.noteContent, this.imageContent, this.logoContent].forEach(el => {
      el?.classList.remove('active');
    });

    document.querySelectorAll('.pzy-custom-image-preview, .pzy-custom-logo-preview').forEach(el => {
      el.style.display = 'none';
    });

    const imgBtn = document.getElementById('pzy-image-upload-initial');
    if (imgBtn) imgBtn.style.display = '';
    const logoBtn = document.getElementById('pzy-logo-upload-initial');
    if (logoBtn) logoBtn.style.display = '';

    this.viewToggle?.querySelectorAll('.pzy-view-tab').forEach(tab => {
      tab.classList.toggle('pzy-toggle-tab--active', tab.dataset.view === 'box');
      tab.setAttribute('aria-selected', tab.dataset.view === 'box' ? 'true' : 'false');
    });

    this.log('Customizations reset');
  }

  resetAllState() {
    this.quantity = 1;
    this.hasCustomNote = false;
    this.hasCustomImage = false;
    this.hasCustomLogo = false;

    if (this.cropper) {
      this.cropper.destroy();
      this.cropper = null;
    }
    if (this.customCropper) {
      this.customCropper.destroy();
      this.customCropper = null;
    }
    this.isRotated = false;
    this.croppedOrientation = false;
    this.croppedImageCanvas = null;
    this.finalPreviewCanvas = null;
    this.puzzlePreviewCanvas = null;
    this.pendingCustomImageFile = null;

    if (this.uploadedImageUrl) {
      URL.revokeObjectURL(this.uploadedImageUrl);
      this.uploadedImageUrl = null;
    }
    this.originalFile = null;

    if (this.fileInput) this.fileInput.value = '';
    if (this.noteTextArea) this.noteTextArea.value = '';
    if (this.noteRow1Count) this.noteRow1Count.textContent = '0';
    if (this.noteRow2Count) this.noteRow2Count.textContent = '0';
    if (this.quantityInput) this.quantityInput.value = '1';

    if (this.noteCheckbox) this.noteCheckbox.checked = false;
    if (this.imageCheckbox) this.imageCheckbox.checked = false;
    if (this.logoCheckbox) this.logoCheckbox.checked = false;
    if (this.rightsCheckbox) this.rightsCheckbox.checked = false;
    if (this.addToCartBtn) this.addToCartBtn.disabled = true;
    this.syncRightsGateState();

    [this.noteContent, this.imageContent, this.logoContent].forEach(el => {
      el?.classList.remove('active');
    });

    this.customImageBlob = null;
    this.customLogoBlob = null;
    this.currentView = 'box';
    if (this.customImageInput) this.customImageInput.value = '';
    if (this.customLogoInput) this.customLogoInput.value = '';

    document.querySelectorAll('.pzy-custom-image-preview, .pzy-custom-logo-preview').forEach(el => {
      el.style.display = 'none';
    });

    const imgBtn2 = document.getElementById('pzy-image-upload-initial');
    if (imgBtn2) imgBtn2.style.display = '';
    const logoBtn2 = document.getElementById('pzy-logo-upload-initial');
    if (logoBtn2) logoBtn2.style.display = '';

    this.viewToggle?.querySelectorAll('.pzy-view-tab').forEach(tab => {
      tab.classList.toggle('pzy-toggle-tab--active', tab.dataset.view === 'box');
      tab.setAttribute('aria-selected', tab.dataset.view === 'box' ? 'true' : 'false');
    });

    const blankPixel = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    if (this.cropImage) this.cropImage.src = blankPixel;
    if (this.previewImage) this.previewImage.src = blankPixel;
    if (this.zoomSlider) this.zoomSlider.value = 0;

    this.bulkManager?.reset();

    this.hideLoading();
    this.log('State reset');
  }
};

/* ========================================================================
   Auto-initialize
   ======================================================================== */

document.addEventListener('DOMContentLoaded', () => {
  if (document.getElementById('pzy-customizer')) {
    window.pzyCustomizer = new PuzzleCustomizer();
  }
});
